import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { portalClientApi } from '../services/api';
import { useToast } from '../context/ToastContext';
import {
  FileText, Download, Calendar, User, LogOut, BookOpen, FolderOpen,
  ClipboardList, RefreshCw, Send, ExternalLink, ChevronLeft, ChevronRight,
  Video, XCircle, Shield, MessageCircle, ArrowLeft, Plus, Clock,
  Menu, X, Home, CreditCard, CheckCircle, BarChart2, Eye, MapPin, ChevronDown,
  MessageSquare, HelpCircle,
} from 'lucide-react';
import StripeCheckout from '../components/StripeCheckout';
import LogoIcon from '../components/LogoIcon';
import './Pages.css';
import './Portal.css';

interface PortalInvoice {
  id: string;
  invoice_number: string;
  invoice_date: string;
  due_date: string;
  subject: string | null;
  total_eur: number;
  status: string;
  total_paid: number;
  remaining: number;
  stripe_available: boolean;
}

interface PortalSlot {
  id: string;
  start_time: string;
  end_time: string;
  location_type: string;
}

interface PortalBooking {
  id: string;
  booking_reference: string;
  status: string;
  notes: string | null;
  created_at: string;
  start_time: string | null;
  end_time: string | null;
  location_type: string | null;
}

interface PortalSettings {
  client_id: string;
  show_sessions: boolean;
  show_documents: boolean;
  show_homework: boolean;
  show_followups: boolean;
  show_invoices: boolean;
  show_bookings: boolean;
  show_support?: boolean;
}

interface ClientProfile {
  id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  street: string | null;
  zip_code: string | null;
  city: string | null;
  country: string | null;
  tax_number: string | null;
  consent_gdpr: boolean;
  consent_processing: boolean;
  settings: PortalSettings | null;
  coach_theme: string | null;
  coach_business_name: string | null;
}

interface PortalSession {
  id: string;
  session_number: number | null;
  scheduled_at: string | null;
  duration_minutes: number | null;
  location_type: string | null;
  status: string | null;
  topics_discussed: string | null;
  homework_assigned: string | null;
  case_id?: string | null;
  case_name?: string | null;
  session_topic: string | null;
  session_results: string | null;
  session_agreements: string | null;
  session_homework_next: string | null;
}

interface PortalDocument {
  id: string;
  title: string | null;
  file_name: string;
  file_type: string | null;
  document_type: string | null;
  created_at: string;
  case_id?: string | null;
  case_name?: string | null;
}

interface PortalHomework {
  session_id: string;
  session_number: number | null;
  scheduled_at: string | null;
  homework_text: string;
  submitted_at?: string | null;
}

interface PortalFollowUp {
  id: string;
  scheduled_date: string | null;
  status: string;
  template_name: string | null;
  token: string | null;
}

interface PortalTicket {
  id: string;
  subject: string;
  message?: string;
  status: string;
  category: string | null;
  created_at: string | null;
  sla_due_at: string | null;
  first_response_at: string | null;
  reply_count?: number;
}

interface PortalTicketReply {
  id: string;
  sender_type: string;
  sender_name: string;
  message: string;
  is_internal: boolean;
  created_at: string | null;
}

interface PortalTicketDetail {
  id: string;
  subject: string;
  message: string;
  status: string;
  category: string | null;
  created_at: string | null;
  sla_due_at: string | null;
  first_response_at: string | null;
  replies: PortalTicketReply[];
}

type TabKey = 'dashboard' | 'rechnungen' | 'termine' | 'sitzungen' | 'followups' | 'nachrichten' | 'profil' | 'datenschutz' | 'hilfe';

const statusLabels: Record<string, string> = {
  sent: 'Offen', paid: 'Bezahlt', partially_paid: 'Teilbezahlt',
  overdue: 'Überfällig', cancelled: 'Storniert',
  confirmed: 'Bestätigt', pending: 'Ausstehend', completed: 'Abgeschlossen',
  scheduled: 'Geplant', in_progress: 'Laufend', no_show: 'Nicht erschienen',
  ausstehend: 'Ausstehend', gesendet: 'Gesendet', beantwortet: 'Beantwortet', abgeschlossen: 'Abgeschlossen',
};

// --- Password Strength Helper ---
function getPasswordStrength(pw: string): { level: number; label: string; color: string } {
  if (!pw || pw.length < 8) return { level: 0, label: 'Zu kurz', color: '#ef4444' };
  if (pw.length < 12) return { level: 1, label: 'Mittel', color: '#f59e0b' };
  const hasSpecial = /[^A-Za-z0-9]/.test(pw);
  if (hasSpecial) return { level: 2, label: 'Stark', color: '#22c55e' };
  return { level: 1, label: 'Mittel', color: '#f59e0b' };
}

function PasswordStrengthBar({ password }: { password: string }) {
  const strength = getPasswordStrength(password);
  if (!password) return null;
  return (
    <div style={{ marginTop: 6 }}>
      <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
        {[0, 1, 2].map((i) => (
          <div key={i} style={{
            flex: 1, height: 4, borderRadius: 2,
            background: i <= strength.level ? strength.color : 'var(--border)',
            transition: 'background 0.2s',
          }} />
        ))}
      </div>
      <span style={{ fontSize: 12, color: strength.color }}>{strength.label}</span>
    </div>
  );
}

// --- Registration Success Screen ---
function RegistrationSuccess({ clientName, onContinue }: { clientName: string; onContinue: () => void }) {
  return (
    <div style={{ textAlign: 'center', padding: '20px 0' }}>
      <div style={{
        width: 64, height: 64, borderRadius: '50%', margin: '0 auto 16px',
        background: 'rgba(34, 197, 94, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <CheckCircle size={36} style={{ color: '#22c55e' }} />
      </div>
      <h3 style={{ color: 'var(--primary-dark)', marginBottom: 8 }}>Willkommen im Portal{clientName ? `, ${clientName.split(' ')[0]}` : ''}!</h3>
      <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 20 }}>
        Ihr Konto wurde erfolgreich eingerichtet. Im Portal können Sie:
      </p>
      <ul style={{ textAlign: 'left', fontSize: 14, color: 'var(--text)', listStyle: 'none', padding: 0, margin: '0 0 24px' }}>
        {[
          'Rechnungen einsehen und online bezahlen',
          'Termine buchen und verwalten',
          'Dokumente herunterladen',
          'Ihren Coach kontaktieren',
        ].map((item, i) => (
          <li key={i} style={{ padding: '6px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
            <CheckCircle size={16} style={{ color: '#22c55e', flexShrink: 0 }} />
            {item}
          </li>
        ))}
      </ul>
      <button className="btn btn-primary" style={{ width: '100%' }} onClick={onContinue}>
        Zum Dashboard
      </button>
    </div>
  );
}

// --- Password Change Prompt (for weak passwords) ---
function PasswordChangePrompt({ email, onDone }: { email: string; onDone: () => void }) {
  const [step, setStep] = useState<'request' | 'reset'>('request');
  const [code, setCode] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleRequestCode = async () => {
    setLoading(true);
    setError('');
    try {
      await portalClientApi.forgotPassword(email);
      setStep('reset');
    } catch {
      setError('Code konnte nicht gesendet werden.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPw.length < 8) { setError('Passwort muss mindestens 8 Zeichen lang sein'); return; }
    if (newPw !== confirmPw) { setError('Passwörter stimmen nicht überein'); return; }
    setLoading(true);
    setError('');
    try {
      await portalClientApi.resetPassword({ email, code, new_password: newPw });
      onDone();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Passwort konnte nicht geändert werden.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{
        width: 56, height: 56, borderRadius: '50%', margin: '0 auto 16px',
        background: 'rgba(245, 158, 11, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Shield size={28} style={{ color: '#f59e0b' }} />
      </div>
      <h3 style={{ color: 'var(--primary-dark)', marginBottom: 8 }}>Passwort aktualisieren</h3>
      <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 20 }}>
        Ihr Passwort entspricht nicht den aktuellen Sicherheitsanforderungen (mind. 8 Zeichen). Bitte ändern Sie es.
      </p>

      {error && <div className="auth-error">{error}</div>}

      {step === 'request' ? (
        <button className="btn btn-primary" style={{ width: '100%' }} onClick={handleRequestCode} disabled={loading}>
          {loading ? 'Wird gesendet...' : 'Reset-Code an ' + email + ' senden'}
        </button>
      ) : (
        <form onSubmit={handleReset} style={{ textAlign: 'left' }}>
          <div className="form-group">
            <label>Reset-Code</label>
            <input
              type="text" inputMode="numeric" pattern="[0-9]{6}" maxLength={6}
              value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              placeholder="6-stelliger Code" required autoFocus
              style={{ fontSize: '24px', letterSpacing: '8px', textAlign: 'center' }}
            />
          </div>
          <div className="form-group">
            <label>Neues Passwort</label>
            <input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="Mind. 8 Zeichen" minLength={8} required />
            <PasswordStrengthBar password={newPw} />
          </div>
          <div className="form-group">
            <label>Passwort bestätigen</label>
            <input type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} placeholder="Passwort wiederholen" minLength={8} required
              style={{ borderColor: confirmPw && confirmPw !== newPw ? '#ef4444' : undefined }} />
            {confirmPw && confirmPw !== newPw && <span style={{ fontSize: 12, color: '#ef4444' }}>Passwörter stimmen nicht überein</span>}
          </div>
          <button type="submit" className="btn btn-primary" style={{ width: '100%' }}
            disabled={loading || code.length !== 6 || newPw.length < 8 || newPw !== confirmPw}>
            {loading ? 'Wird gespeichert...' : 'Passwort ändern'}
          </button>
        </form>
      )}
    </div>
  );
}

// --- Portal Login ---
export function PortalLogin() {
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState<'login' | 'register' | 'forgot'>('login');
  const [loginMethod, setLoginMethod] = useState<'password' | 'code'>('password');
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [resetStep, setResetStep] = useState<'email' | 'code' | 'done'>('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [code, setCode] = useState('');
  const [token, setToken] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  // Register-specific states
  const [codeValid, setCodeValid] = useState(false);
  const [clientInfo, setClientInfo] = useState<{ client_name: string; masked_email: string } | null>(null);
  const [codeError, setCodeError] = useState('');
  const [codeChecking, setCodeChecking] = useState(false);
  const [showResend, setShowResend] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  const [resendMessage, setResendMessage] = useState('');
  const [registerSuccess, setRegisterSuccess] = useState(false);
  const [registeredName, setRegisteredName] = useState('');

  // Auto-switch to register tab if URL has ?register=true&code=XXX
  useEffect(() => {
    if (searchParams.get('register') === 'true') {
      setMode('register');
      const urlCode = searchParams.get('code');
      if (urlCode) {
        setToken(urlCode);
      }
    }
  }, [searchParams]);

  // Auto-validate when token is set from URL
  useEffect(() => {
    if (token && searchParams.get('register') === 'true' && searchParams.get('code')) {
      validateToken(token);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const validateToken = useCallback(async (t: string) => {
    if (!t || t.length < 10) return;
    setCodeChecking(true);
    setCodeError('');
    setCodeValid(false);
    setClientInfo(null);
    try {
      const res = await portalClientApi.validateCode(t);
      setCodeValid(true);
      setClientInfo({ client_name: res.data.client_name, masked_email: res.data.masked_email });
    } catch (err: any) {
      const detail = err.response?.data?.detail || 'Code ungültig';
      setCodeError(detail);
      setCodeValid(false);
      if (detail.toLowerCase().includes('abgelaufen')) {
        setShowResend(true);
      }
    } finally {
      setCodeChecking(false);
    }
  }, []);

  const [passwordChangeRequired, setPasswordChangeRequired] = useState(false);

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await portalClientApi.login({ email, password });
      localStorage.setItem('portal_token', res.data.access_token);
      if (res.data.password_change_required) {
        setPasswordChangeRequired(true);
      } else {
        window.location.replace('/portal');
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Anmeldung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');
    try {
      await portalClientApi.requestCode(email);
      setStep('code');
      setMessage('Ein 6-stelliger Code wurde an Ihre E-Mail gesendet.');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Code konnte nicht gesendet werden.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await portalClientApi.verifyCode(email, code);
      localStorage.setItem('portal_token', res.data.access_token);
      window.location.replace('/portal');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Anmeldung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const handleResendLoginCode = async () => {
    setError('');
    setMessage('');
    setLoading(true);
    try {
      await portalClientApi.requestCode(email);
      setMessage('Neuer Code wurde gesendet.');
      setCode('');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Code konnte nicht gesendet werden.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 8) { setError('Passwort muss mindestens 8 Zeichen lang sein'); return; }
    if (password !== confirmPassword) { setError('Passwörter stimmen nicht überein'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await portalClientApi.register({ token, password });
      localStorage.setItem('portal_token', res.data.access_token);
      setRegisteredName(clientInfo?.client_name || '');
      setRegisterSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Registrierung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const handleResendInvitation = async () => {
    if (!resendEmail) return;
    setLoading(true);
    setResendMessage('');
    try {
      const res = await portalClientApi.resendInvitation(resendEmail);
      setResendMessage(res.data.message || 'Falls eine Einladung vorliegt, wurde ein neuer Code gesendet.');
    } catch {
      setResendMessage('Falls eine Einladung vorliegt, wurde ein neuer Code gesendet.');
    } finally {
      setLoading(false);
    }
  };

  const switchLoginMethod = (m: 'password' | 'code') => {
    setLoginMethod(m);
    setStep('email');
    setError('');
    setMessage('');
    setCode('');
    setPassword('');
  };

  const handleForgotRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');
    try {
      await portalClientApi.forgotPassword(email);
      setResetStep('code');
      setMessage('Ein 6-stelliger Reset-Code wurde an Ihre E-Mail gesendet (falls ein Konto existiert).');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Code konnte nicht gesendet werden.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 8) { setError('Passwort muss mindestens 8 Zeichen lang sein'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await portalClientApi.resetPassword({ email, code, new_password: newPassword });
      setResetStep('done');
      setMessage(res.data.message || 'Passwort wurde erfolgreich geändert.');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Passwort konnte nicht geändert werden.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="card" style={{ width: 420, padding: 32 }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <LogoIcon size={36} />
          <h2 style={{ color: 'var(--primary-dark)' }}>Klienten-Portal</h2>
          <p className="text-muted text-sm">CoachDesk365</p>
        </div>

        {mode !== 'forgot' && !registerSuccess && (
          <div className="category-chips" style={{ marginBottom: 16, justifyContent: 'center' }}>
            <button className={`chip ${mode === 'login' ? 'chip-active' : ''}`} onClick={() => { setMode('login'); setStep('email'); setError(''); setMessage(''); }}>Anmelden</button>
            <button className={`chip ${mode === 'register' ? 'chip-active' : ''}`} onClick={() => { setMode('register'); setError(''); setMessage(''); }}>Registrieren</button>
          </div>
        )}
        {mode === 'forgot' && (
          <p style={{ textAlign: 'center', marginBottom: 16, color: 'var(--text-muted)', fontSize: '14px' }}>Passwort zurücksetzen</p>
        )}

        {error && <div className="auth-error">{error}</div>}
        {message && !registerSuccess && <div style={{ background: 'rgba(var(--success-rgb, 91, 140, 90), 0.1)', color: 'var(--success, #5B8C5A)', padding: '10px 14px', borderRadius: '6px', marginBottom: '16px', fontSize: '14px' }}>{message}</div>}

        {passwordChangeRequired ? (
          <PasswordChangePrompt email={email} onDone={() => { setPasswordChangeRequired(false); setMode('login'); setLoginMethod('password'); setPassword(''); setError(''); setMessage('Passwort wurde geändert. Bitte melden Sie sich erneut an.'); }} />
        ) : registerSuccess ? (
          <RegistrationSuccess clientName={registeredName} onContinue={() => window.location.replace('/portal')} />
        ) : mode === 'login' ? (
          <>
            {loginMethod === 'password' ? (
              <form onSubmit={handlePasswordLogin} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handlePasswordLogin(e); } }}>
                <div className="form-group">
                  <label>E-Mail</label>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="ihre@email.de" required autoFocus />
                </div>
                <div className="form-group">
                  <label>Passwort</label>
                  <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Ihr Passwort" required />
                </div>
                <button type="submit" className="btn btn-primary" style={{ width: '100%' }} disabled={loading}>
                  {loading ? 'Anmelden...' : 'Anmelden'}
                </button>
                <div style={{ textAlign: 'right', marginTop: '8px' }}>
                  <button type="button" onClick={() => { setMode('forgot'); setResetStep('email'); setError(''); setMessage(''); setCode(''); setNewPassword(''); }}
                    style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '13px' }}>
                    Passwort vergessen?
                  </button>
                </div>
              </form>
            ) : step === 'email' ? (
              <form onSubmit={handleRequestCode}>
                <div className="form-group">
                  <label>E-Mail</label>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="ihre@email.de" required autoFocus />
                </div>
                <button type="submit" className="btn btn-primary" style={{ width: '100%' }} disabled={loading}>
                  {loading ? 'Wird gesendet...' : 'Code senden'}
                </button>
              </form>
            ) : (
              <form onSubmit={handleVerifyCode}>
                <div className="form-group">
                  <label>Anmeldecode</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="6-stelliger Code"
                    required
                    autoFocus
                    style={{ fontSize: '24px', letterSpacing: '8px', textAlign: 'center' }}
                  />
                </div>
                <button type="submit" className="btn btn-primary" style={{ width: '100%' }} disabled={loading || code.length !== 6}>
                  {loading ? 'Wird geprüft...' : 'Anmelden'}
                </button>
                <div style={{ textAlign: 'center', marginTop: '12px' }}>
                  <button type="button" onClick={handleResendLoginCode} disabled={loading}
                    style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontSize: '14px' }}>
                    Code erneut senden
                  </button>
                  <span style={{ margin: '0 8px', color: 'var(--border)' }}>|</span>
                  <button type="button" onClick={() => { setStep('email'); setCode(''); setError(''); setMessage(''); }}
                    style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '14px' }}>
                    Andere E-Mail
                  </button>
                </div>
              </form>
            )}
            <div style={{ textAlign: 'center', marginTop: '16px', borderTop: '1px solid var(--border)', paddingTop: '16px' }}>
              <button type="button" onClick={() => switchLoginMethod(loginMethod === 'password' ? 'code' : 'password')}
                style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontSize: '14px' }}>
                {loginMethod === 'password' ? 'Stattdessen mit E-Mail-Code anmelden' : 'Stattdessen mit Passwort anmelden'}
              </button>
            </div>
          </>
        ) : mode === 'register' ? (
          <form onSubmit={handleRegister}>
            <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 16, textAlign: 'center' }}>
              Geben Sie Ihren Einladungscode ein, um Ihr Konto zu aktivieren.
            </p>

            <div className="form-group">
              <label>Einladungscode</label>
              <input
                value={token}
                onChange={(e) => { setToken(e.target.value); setCodeValid(false); setCodeError(''); setClientInfo(null); setShowResend(false); }}
                onBlur={() => validateToken(token)}
                placeholder="Code aus der Einladungs-E-Mail"
                required
                style={{ borderColor: codeValid ? '#22c55e' : codeError ? '#ef4444' : undefined }}
              />
              {codeChecking && <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Code wird geprüft...</span>}
              {codeError && (
                <div style={{ fontSize: 13, color: '#ef4444', marginTop: 4 }}>
                  {codeError}
                  {codeError.toLowerCase().includes('abgelaufen') && (
                    <button type="button" onClick={() => setShowResend(true)}
                      style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontSize: 13, marginLeft: 4 }}>
                      Neuen Code anfordern
                    </button>
                  )}
                </div>
              )}
              <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                Der Code ist 24 Stunden gültig.
              </p>
            </div>

            {codeValid && clientInfo && (
              <div style={{
                background: 'rgba(34, 197, 94, 0.08)', border: '1px solid rgba(34, 197, 94, 0.3)',
                borderRadius: 8, padding: '12px 14px', marginBottom: 16,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#16a34a', fontSize: 14, fontWeight: 500 }}>
                  <CheckCircle size={16} /> Einladung für: {clientInfo.client_name}
                </div>
                <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>
                  E-Mail: {clientInfo.masked_email}
                </div>
              </div>
            )}

            {codeValid && (
              <>
                <div className="form-group">
                  <label>Passwort wählen</label>
                  <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Mind. 8 Zeichen" minLength={8} required />
                  <PasswordStrengthBar password={password} />
                </div>
                <div className="form-group">
                  <label>Passwort bestätigen</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Passwort wiederholen"
                    minLength={8}
                    required
                    style={{ borderColor: confirmPassword && confirmPassword !== password ? '#ef4444' : undefined }}
                  />
                  {confirmPassword && confirmPassword !== password && (
                    <span style={{ fontSize: 12, color: '#ef4444' }}>Passwörter stimmen nicht überein</span>
                  )}
                </div>
                <button type="submit" className="btn btn-primary" style={{ width: '100%' }}
                  disabled={loading || !codeValid || password.length < 8 || password !== confirmPassword}>
                  {loading ? 'Wird eingerichtet...' : 'Konto aktivieren'}
                </button>
              </>
            )}

            {!codeValid && !codeChecking && (
              <div style={{ textAlign: 'center', marginTop: 16, borderTop: '1px solid var(--border)', paddingTop: 16 }}>
                <button type="button" onClick={() => setShowResend(true)}
                  style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 13 }}>
                  Code nicht erhalten?
                </button>
              </div>
            )}

            {showResend && (
              <div style={{ marginTop: 12, padding: 12, background: 'var(--bg)', borderRadius: 8, border: '1px solid var(--border)' }}>
                <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 8 }}>
                  Geben Sie Ihre E-Mail-Adresse ein, um einen neuen Einladungscode zu erhalten.
                </p>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    type="email"
                    value={resendEmail}
                    onChange={(e) => setResendEmail(e.target.value)}
                    placeholder="ihre@email.de"
                    style={{ flex: 1 }}
                  />
                  <button type="button" className="btn btn-primary" onClick={handleResendInvitation}
                    disabled={loading || !resendEmail} style={{ whiteSpace: 'nowrap' }}>
                    {loading ? '...' : 'Senden'}
                  </button>
                </div>
                {resendMessage && (
                  <p style={{ fontSize: 13, color: 'var(--success, #5B8C5A)', marginTop: 8 }}>{resendMessage}</p>
                )}
              </div>
            )}
          </form>
        ) : (
          /* Passwort vergessen Flow */
          <>
            {resetStep === 'email' ? (
              <form onSubmit={handleForgotRequest}>
                <div className="form-group">
                  <label>E-Mail-Adresse</label>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="ihre@email.de" required autoFocus />
                </div>
                <button type="submit" className="btn btn-primary" style={{ width: '100%' }} disabled={loading}>
                  {loading ? 'Wird gesendet...' : 'Reset-Code senden'}
                </button>
              </form>
            ) : resetStep === 'code' ? (
              <form onSubmit={handleResetPassword}>
                <div className="form-group">
                  <label>Reset-Code</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="6-stelliger Code"
                    required
                    autoFocus
                    style={{ fontSize: '24px', letterSpacing: '8px', textAlign: 'center' }}
                  />
                </div>
                <div className="form-group">
                  <label>Neues Passwort (mind. 8 Zeichen)</label>
                  <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} minLength={8} required />
                </div>
                <button type="submit" className="btn btn-primary" style={{ width: '100%' }} disabled={loading || code.length !== 6}>
                  {loading ? 'Wird gespeichert...' : 'Passwort ändern'}
                </button>
                <div style={{ textAlign: 'center', marginTop: '12px' }}>
                  <button type="button" onClick={() => { handleForgotRequest({ preventDefault: () => {} } as React.FormEvent); }}
                    disabled={loading}
                    style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontSize: '14px' }}>
                    Code erneut senden
                  </button>
                </div>
              </form>
            ) : (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <div style={{
                  width: 64, height: 64, borderRadius: '50%', margin: '0 auto 16px',
                  background: 'rgba(34, 197, 94, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <CheckCircle size={36} style={{ color: '#22c55e' }} />
                </div>
                <p style={{ fontSize: '15px', marginBottom: '20px' }}>Ihr Passwort wurde erfolgreich geändert.</p>
                <button className="btn btn-primary" style={{ width: '100%' }}
                  onClick={() => { setMode('login'); setLoginMethod('password'); setError(''); setMessage(''); setPassword(''); setCode(''); setNewPassword(''); }}>
                  Jetzt anmelden
                </button>
              </div>
            )}
            {resetStep !== 'done' && (
              <div style={{ textAlign: 'center', marginTop: '16px', borderTop: '1px solid var(--border)', paddingTop: '16px' }}>
                <button type="button" onClick={() => { setMode('login'); setError(''); setMessage(''); setCode(''); setNewPassword(''); }}
                  style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontSize: '14px' }}>
                  ← Zurück zur Anmeldung
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}


// --- Portal Setup (first-time password via token) ---
export function PortalSetup() {
  const { token } = useParams<{ token: string }>();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [clientName, setClientName] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [tokenValid, setTokenValid] = useState<boolean | null>(null);
  const [tokenError, setTokenError] = useState('');
  const [registerSuccess, setRegisterSuccess] = useState(false);

  // Validate token on mount
  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const res = await portalClientApi.validateCode(token);
        setTokenValid(true);
        setClientName(res.data.client_name);
        setMaskedEmail(res.data.masked_email);
      } catch (err: any) {
        setTokenValid(false);
        setTokenError(err.response?.data?.detail || 'Einladungscode ungültig');
      }
    })();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 8) { setError('Passwort muss mindestens 8 Zeichen lang sein'); return; }
    if (password !== confirm) { setError('Passwörter stimmen nicht überein'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await portalClientApi.register({ token: token!, password });
      localStorage.setItem('portal_token', res.data.access_token);
      setRegisterSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Registrierung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="card" style={{ width: 420, padding: 32 }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <LogoIcon size={36} />
          <h2 style={{ color: 'var(--primary-dark)' }}>Portal einrichten</h2>
          {tokenValid === null && <p className="text-muted text-sm">Code wird geprüft...</p>}
          {tokenValid === true && clientName && (
            <p style={{ color: 'var(--text)', fontSize: 15, marginTop: 8 }}>
              Hallo {clientName.split(' ')[0]}, wählen Sie Ihr Passwort.
            </p>
          )}
        </div>

        {registerSuccess ? (
          <RegistrationSuccess clientName={clientName} onContinue={() => window.location.replace('/portal')} />
        ) : tokenValid === false ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: 56, height: 56, borderRadius: '50%', margin: '0 auto 16px',
              background: 'rgba(239, 68, 68, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <XCircle size={28} style={{ color: '#ef4444' }} />
            </div>
            <p style={{ color: '#ef4444', fontSize: 14, marginBottom: 16 }}>{tokenError}</p>
            <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 20 }}>
              Möglicherweise ist der Code abgelaufen oder wurde bereits verwendet.
            </p>
            <a href="/portal/login?register=true" className="btn btn-primary" style={{ display: 'inline-block', textDecoration: 'none' }}>
              Zur Registrierung
            </a>
          </div>
        ) : tokenValid === true ? (
          <>
            {maskedEmail && (
              <div style={{
                background: 'rgba(34, 197, 94, 0.08)', border: '1px solid rgba(34, 197, 94, 0.3)',
                borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: 'var(--text-muted)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#16a34a', fontWeight: 500 }}>
                  <CheckCircle size={14} /> Einladung gültig
                </div>
                <div style={{ marginTop: 4 }}>E-Mail: {maskedEmail}</div>
              </div>
            )}

            {error && <div className="auth-error">{error}</div>}

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Passwort wählen</label>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Mind. 8 Zeichen" minLength={8} required autoFocus />
                <PasswordStrengthBar password={password} />
              </div>
              <div className="form-group">
                <label>Passwort bestätigen</label>
                <input
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Passwort wiederholen"
                  minLength={8}
                  required
                  style={{ borderColor: confirm && confirm !== password ? '#ef4444' : undefined }}
                />
                {confirm && confirm !== password && (
                  <span style={{ fontSize: 12, color: '#ef4444' }}>Passwörter stimmen nicht überein</span>
                )}
              </div>
              <button type="submit" className="btn btn-primary" style={{ width: '100%' }}
                disabled={loading || password.length < 8 || password !== confirm}>
                {loading ? 'Wird eingerichtet...' : 'Konto aktivieren'}
              </button>
            </form>
          </>
        ) : null}
      </div>
    </div>
  );
}


// --- Portal Dashboard ---
export default function PortalDashboard() {
  const { showToast } = useToast();
  const [profile, setProfile] = useState<ClientProfile | null>(null);
  const [invoices, setInvoices] = useState<PortalInvoice[]>([]);
  const [slots, setSlots] = useState<PortalSlot[]>([]);
  const [bookings, setBookings] = useState<PortalBooking[]>([]);
  const [sessions, setSessions] = useState<PortalSession[]>([]);
  const [documents, setDocuments] = useState<PortalDocument[]>([]);
  const [homework, setHomework] = useState<PortalHomework[]>([]);
  const [followups, setFollowups] = useState<PortalFollowUp[]>([]);
  const [tab, setTab] = useState<TabKey>('profil');
  const [chatMessages, setChatMessages] = useState<any[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [bookingNote, setBookingNote] = useState('');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<PortalSlot | null>(null);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState<string | null>(null);
  const [hwResponses, setHwResponses] = useState<Record<string, string>>({});
  const [hwSubmitting, setHwSubmitting] = useState<string | null>(null);
  const [cancelLoading, setCancelLoading] = useState<string | null>(null);
  // Änderungsanträge
  const [changeRequests, setChangeRequests] = useState<any[]>([]);
  const [changeModal, setChangeModal] = useState<{ field: string; label: string; currentValue: string } | null>(null);
  const [changeNewValue, setChangeNewValue] = useState('');
  const [changeReason, setChangeReason] = useState('');
  const [changeSubmitting, setChangeSubmitting] = useState(false);
  const [addressForm, setAddressForm] = useState({ street: '', zip_code: '', city: '', country: '' });
  // Support / Hilfe
  const [supportTickets, setSupportTickets] = useState<PortalTicket[]>([]);
  const [supportView, setSupportView] = useState<'list' | 'create' | 'detail'>('list');
  const [supportDetail, setSupportDetail] = useState<PortalTicketDetail | null>(null);
  const [supportForm, setSupportForm] = useState({ subject: '', message: '', category: 'sonstiges' });
  const [supportReply, setSupportReply] = useState('');
  const [supportLoading, setSupportLoading] = useState(false);

  // NEU: Sidebar State
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // PDF-Vorschau
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [previewTitle, setPreviewTitle] = useState('');
  const [previewIsPdf, setPreviewIsPdf] = useState(false);
  // Stripe-Bezahlung
  const [payInvoice, setPayInvoice] = useState<PortalInvoice | null>(null);
  const [showPastBookings, setShowPastBookings] = useState(false);
  const [expandedSessions, setExpandedSessions] = useState<Set<string>>(new Set());

  const toggleSession = (sessionId: string) => {
    setExpandedSessions(prev => {
      const next = new Set(prev);
      if (next.has(sessionId)) next.delete(sessionId);
      else next.add(sessionId);
      return next;
    });
  };

  const settings = profile?.settings;

  useEffect(() => {
    const token = localStorage.getItem('portal_token');
    if (!token) { window.location.replace('/portal/login'); return; }
    loadProfile();
  }, []);

  // Navigate to a tab AND push a history entry (for back/forward navigation)
  const navigateTab = (key: TabKey) => {
    if (key === tab) return;
    window.history.pushState({ tab: key }, '', `/portal#${key}`);
    setTab(key);
  };

  // Once profile/settings loaded, read initial tab from URL hash or default to dashboard
  useEffect(() => {
    if (!profile) return;
    const hash = window.location.hash.replace('#', '');
    const tabRedirects: Record<string, string> = { dokumente: 'sitzungen', hausaufgaben: 'sitzungen' };
    const resolved = tabRedirects[hash] || hash;
    const validTabs: TabKey[] = ['dashboard','rechnungen','termine','sitzungen','followups','nachrichten','profil','datenschutz','hilfe'];
    const initialTab = validTabs.includes(resolved as TabKey) ? (resolved as TabKey) : 'dashboard';
    window.history.replaceState({ tab: initialTab }, '', `/portal#${initialTab}`);
    setTab(initialTab);
    loadContent();
  }, [profile]);

  // Listen for browser back/forward navigation
  useEffect(() => {
    const onPopState = (e: PopStateEvent) => {
      const t = e.state?.tab as TabKey;
      if (t) setTab(t);
      else setTab('dashboard');
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const loadProfile = async () => {
    try {
      const res = await portalClientApi.profile();
      setProfile(res.data);
      // Coach-Theme auf das Portal anwenden
      if (res.data.coach_theme) {
        document.documentElement.setAttribute('data-theme', res.data.coach_theme);
      }
    } catch {
      localStorage.removeItem('portal_token');
      window.location.replace('/portal/login');
    }
    // Änderungsanträge laden
    try {
      const crRes = await portalClientApi.changeRequests();
      setChangeRequests(crRes.data);
    } catch {}
  };

  const loadContent = async () => {
    const s = profile?.settings;
    // Always load invoices + bookings (existing behavior, guarded in UI)
    try { const res = await portalClientApi.invoices(); setInvoices(res.data); } catch {}
    try { const res = await portalClientApi.slots(); setSlots(res.data); } catch {}
    try { const res = await portalClientApi.bookings(); setBookings(res.data); } catch {}
    if (!s || s.show_sessions) {
      try { const res = await portalClientApi.sessions(); setSessions(res.data); } catch {}
    }
    // Always load documents + homework (Dashboard counts need them)
    try { const res = await portalClientApi.documents(); setDocuments(res.data); } catch {}
    try { const res = await portalClientApi.homework(); setHomework(res.data); } catch {}
    if (!s || s.show_followups) {
      try { const res = await portalClientApi.followups(); setFollowups(res.data); } catch {}
    }
    if (!s || s.show_support !== false) {
      try { const res = await portalClientApi.supportTickets(); setSupportTickets(res.data); } catch {}
    }
    // Chat messages
    try { const res = await portalClientApi.chatMessages(); setChatMessages(res.data.items || []); } catch {}
  };

  const handleLogout = () => {
    localStorage.removeItem('portal_token');
    window.location.replace('/portal/login');
  };

  const handleDownloadPdf = async (id: string, number: string) => {
    try {
      const res = await portalClientApi.invoicePdf(id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { showToast('PDF-Download fehlgeschlagen', 'error'); }
  };

  const handleBook = async () => {
    if (!selectedSlot) return;
    setBookingLoading(true);
    try {
      const res = await portalClientApi.createBooking({ booking_slot_id: selectedSlot.id, notes: bookingNote || undefined });
      setBookingSuccess(res.data.booking_reference);
      setBookingNote('');
      setSelectedSlot(null);
      setSelectedDate(null);
      // Reload slots + bookings
      try { const slotsRes = await portalClientApi.slots(); setSlots(slotsRes.data); } catch {}
      try { const bookRes = await portalClientApi.bookings(); setBookings(bookRes.data); } catch {}
    } catch (err: any) {
      showToast(err.response?.data?.detail || 'Buchung fehlgeschlagen', 'error');
    } finally {
      setBookingLoading(false);
    }
  };

  const handleDownloadDoc = async (id: string, fileName: string) => {
    try {
      const res = await portalClientApi.downloadDocument(id);
      const blob = new Blob([res.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);
    } catch { showToast('Download fehlgeschlagen', 'error'); }
  };

  const handlePreview = async (type: 'invoice' | 'document', id: string, title: string, fileName?: string) => {
    try {
      const res = type === 'invoice'
        ? await portalClientApi.invoicePdf(id)
        : await portalClientApi.downloadDocument(id);

      // Determine MIME type from response or filename
      const contentType = res.headers?.['content-type'] || '';
      const isPdf = contentType.includes('pdf') || type === 'invoice' || fileName?.endsWith('.pdf');
      const isHtml = contentType.includes('html') || fileName?.endsWith('.html');
      const isImage = contentType.includes('image') || /\.(png|jpg|jpeg|gif|webp|svg)$/i.test(fileName || '');

      if (isPdf || isHtml || isImage) {
        if (isHtml) {
          // HTML: Theme-Farben injizieren und als srcDoc anzeigen
          const text = typeof res.data === 'string' ? res.data : await new Blob([res.data]).text();
          const s = getComputedStyle(document.documentElement);
          const primary = s.getPropertyValue('--primary').trim();
          const primaryDark = s.getPropertyValue('--primary-dark').trim();
          const bg = s.getPropertyValue('--bg').trim();
          const border = s.getPropertyValue('--border').trim();
          let html = text;
          if (primary) {
            const overrides = `<style>
              h1 { color: ${primary} !important; border-bottom-color: ${primaryDark} !important; }
              h2 { color: ${primary} !important; border-bottom-color: ${border} !important; }
              .label { color: ${primary} !important; }
              .info-box { background: ${bg} !important; border-left-color: ${primaryDark} !important; }
              .signatures { border-top-color: ${primary} !important; }
              .sig-block p { color: ${primary} !important; }
              .footer { border-top-color: ${border} !important; }
              .no-print button { background: ${primaryDark} !important; }
            </style>`;
            html = text.replace('</head>', overrides + '</head>');
          }
          setPreviewHtml(html);
          setPreviewUrl(null);
          setPreviewIsPdf(false);
          setPreviewTitle(title);
        } else {
          const mimeType = isPdf ? 'application/pdf' : contentType || 'image/png';
          const blob = new Blob([res.data], { type: mimeType });
          const url = URL.createObjectURL(blob);
          setPreviewHtml(null);
          setPreviewUrl(url);
          setPreviewIsPdf(isPdf);
          setPreviewTitle(title);
        }
      } else {
        // Non-previewable files: direct download
        const blob = new Blob([res.data]);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName || `${title}`;
        a.click();
        URL.revokeObjectURL(url);
        showToast('Datei heruntergeladen', 'success');
      }
    } catch (err: any) {
      const detail = (err.response?.status === 404)
        ? 'Datei nicht gefunden'
        : 'Vorschau konnte nicht geladen werden';
      showToast(detail, 'error');
    }
  };

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewHtml(null);
    setPreviewIsPdf(false);
    setPreviewTitle('');
  };

  const handleSubmitHomework = async (sessionId: string) => {
    const text = hwResponses[sessionId];
    if (!text?.trim()) return;
    setHwSubmitting(sessionId);
    try {
      await portalClientApi.submitHomework(sessionId, { response_text: text });
      setHwResponses((prev) => ({ ...prev, [sessionId]: '' }));
      showToast('Antwort eingereicht!', 'success');
    } catch (err: any) {
      showToast(err.response?.data?.detail || 'Einreichung fehlgeschlagen', 'error');
    } finally {
      setHwSubmitting(null);
    }
  };

  const handleCancelSession = async (sessionId: string) => {
    if (!window.confirm('Möchten Sie diesen Termin wirklich stornieren?')) return;
    setCancelLoading(sessionId);
    try {
      await portalClientApi.cancelSession(sessionId);
      // Reload sessions
      try { const res = await portalClientApi.sessions(); setSessions(res.data); } catch {}
    } catch (err: any) {
      showToast(err.response?.data?.detail || 'Stornierung fehlgeschlagen', 'error');
    } finally {
      setCancelLoading(null);
    }
  };

  const fmt = (n: number) => n.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
  const fmtDate = (d: string) => new Date(d).toLocaleDateString('de-DE');
  const fmtDateTime = (d: string) => new Date(d).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  // Calendar helpers
  const toLocalDateStr = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  const monthNames = [
    'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
    'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
  ];

  const generateMonthGrid = () => {
    const y = currentMonth.getFullYear();
    const m = currentMonth.getMonth();
    const first = new Date(y, m, 1);
    const startDay = (first.getDay() + 6) % 7;
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    const days: { date: Date; isCurrentMonth: boolean }[] = [];
    for (let i = startDay - 1; i >= 0; i--) {
      days.push({ date: new Date(y, m, -i), isCurrentMonth: false });
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ date: new Date(y, m, i), isCurrentMonth: true });
    }
    // Always fill to 42 cells (6 rows) for consistent calendar height
    while (days.length < 42) {
      const last = days[days.length - 1].date;
      const next = new Date(last);
      next.setDate(next.getDate() + 1);
      days.push({ date: next, isCurrentMonth: false });
    }
    return days;
  };

  const getAvailableDates = () => {
    const dates = new Set<string>();
    slots.forEach((s) => dates.add(s.start_time.split('T')[0]));
    return dates;
  };

  const getSlotsForDate = (dateStr: string) => {
    return slots.filter((s) => s.start_time.startsWith(dateStr));
  };

  const availableDates = getAvailableDates();
  const today = toLocalDateStr(new Date());

  const fmtTime = (iso: string) =>
    new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });

  if (!profile) return <div style={{ padding: 40, textAlign: 'center' }}>Wird geladen...</div>;

  // Dashboard computed values
  const openHw = homework.filter((h: any) => !h.submitted_at);
  const completedSessions = sessions.filter((s) => s.status === 'completed');
  const nextSession = sessions.find((s) => s.status === 'scheduled' && s.scheduled_at && new Date(s.scheduled_at) > new Date());
  const pendingInvoices = invoices.filter((i) => ['sent', 'overdue', 'partially_paid'].includes(i.status));
  const initials = `${(profile.first_name || '')[0] || ''}${(profile.last_name || '')[0] || ''}`.toUpperCase();

  // Sidebar navigation items
  const navSections = [
    {
      label: 'Übersicht',
      items: [
        { key: 'dashboard', label: 'Dashboard', icon: <Home size={16} />, badge: 0 },
        ...((!settings || settings.show_bookings) ? [{ key: 'termine', label: 'Termine', icon: <Calendar size={16} />, badge: slots.length }] : []),
        ...((!settings || settings.show_sessions) ? [{ key: 'sitzungen', label: 'Sitzungen', icon: <BookOpen size={16} />, badge: openHw.length }] : []),
        { key: 'nachrichten', label: 'Nachrichten', icon: <MessageSquare size={16} />, badge: 0 },
      ],
    },
    {
      label: 'Materialien',
      items: [
        ...((!settings || settings.show_followups) ? [{ key: 'followups', label: 'Follow-ups', icon: <RefreshCw size={16} />, badge: 0 }] : []),
      ],
    },
    {
      label: 'Verwaltung',
      items: [
        ...((!settings || settings.show_invoices) ? [{ key: 'rechnungen', label: 'Rechnungen', icon: <CreditCard size={16} />, badge: pendingInvoices.length }] : []),
        { key: 'profil', label: 'Profil', icon: <User size={16} />, badge: 0 },
        { key: 'datenschutz', label: 'Datenschutz', icon: <Shield size={16} />, badge: 0 },
        ...((!settings || settings.show_support !== false) ? [{ key: 'hilfe', label: 'Hilfe', icon: <MessageCircle size={16} />, badge: 0 }] : []),
      ],
    },
  ];

  // Greeting
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Guten Morgen' : hour < 18 ? 'Guten Tag' : 'Guten Abend';
  const todayStr = new Date().toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  // Dashboard content renderer
  const renderDashboard = () => (
    <>
      {/* Welcome Banner */}
      <div className="portal-welcome">
        <span className="portal-welcome-date">{todayStr}</span>
        <div className="portal-welcome-greeting">{greeting},</div>
        <div className="portal-welcome-name">{profile.first_name} {profile.last_name}</div>
        <div className="portal-welcome-message">
          {nextSession && nextSession.scheduled_at ? (
            <>Ihr nächster Coaching-Termin ist am <strong>{fmtDateTime(nextSession.scheduled_at)}</strong>. </>
          ) : (
            <>Willkommen in Ihrem persönlichen Coaching-Portal. </>
          )}
          {openHw.length > 0 && <>Sie haben {openHw.length} offene Hausaufgabe{openHw.length > 1 ? 'n' : ''}.</>}
        </div>
      </div>

      {/* Quick Access */}
      <div className="portal-quick-grid">
        <div className="portal-quick-card" onClick={() => navigateTab('termine' as TabKey)}>
          <div className="portal-quick-icon portal-quick-icon-primary"><Calendar size={20} /></div>
          <div className="portal-quick-label">Nächster Termin</div>
          <div className="portal-quick-count">
            {nextSession?.scheduled_at ? fmtDate(nextSession.scheduled_at) : 'Keiner geplant'}
          </div>
        </div>
        <div className="portal-quick-card" onClick={() => navigateTab('sitzungen' as TabKey)}>
          <div className="portal-quick-icon portal-quick-icon-success"><BookOpen size={20} /></div>
          <div className="portal-quick-label">Sitzungen</div>
          <div className="portal-quick-count">{completedSessions.length} abgeschlossen</div>
        </div>
        <div className="portal-quick-card" onClick={() => navigateTab('sitzungen' as TabKey)}>
          {openHw.length > 0 && <span className="portal-quick-dot" />}
          <div className="portal-quick-icon portal-quick-icon-accent"><ClipboardList size={20} /></div>
          <div className="portal-quick-label">Hausaufgaben</div>
          <div className="portal-quick-count">{openHw.length} offen</div>
        </div>
        <div className="portal-quick-card" onClick={() => navigateTab('sitzungen' as TabKey)}>
          <div className="portal-quick-icon portal-quick-icon-info"><FolderOpen size={20} /></div>
          <div className="portal-quick-label">Dokumente</div>
          <div className="portal-quick-count">{documents.length} verfügbar</div>
        </div>
        <div className="portal-quick-card" onClick={() => navigateTab('rechnungen' as TabKey)}>
          {pendingInvoices.length > 0 && <span className="portal-quick-dot" />}
          <div className="portal-quick-icon portal-quick-icon-primary"><CreditCard size={20} /></div>
          <div className="portal-quick-label">Rechnungen</div>
          <div className="portal-quick-count">
            {pendingInvoices.length > 0
              ? fmt(pendingInvoices.reduce((sum, inv) => sum + (inv.remaining > 0 ? inv.remaining : inv.total_eur), 0)) + ' offen'
              : invoices.length > 0
                ? fmt(invoices.reduce((sum, inv) => sum + inv.total_eur, 0)) + ' gesamt'
                : 'Keine'}
          </div>
        </div>
      </div>

      {/* 2-Column Grid */}
      <div className="portal-content-grid">
        {/* Next Appointment */}
        <div className="portal-section-box">
          <div className="portal-section-header">
            <span className="portal-section-title"><Calendar size={16} /> Nächster Termin</span>
            <button className="portal-section-link" onClick={() => navigateTab('termine' as TabKey)}>Alle Termine &rarr;</button>
          </div>
          <div className="portal-section-body">
            {nextSession && nextSession.scheduled_at ? (() => {
              const d = new Date(nextSession.scheduled_at);
              const monthShort = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
              return (
                <div className="portal-next-appt">
                  <div className="portal-appt-date-box">
                    <span className="portal-appt-day">{d.getDate()}</span>
                    <span className="portal-appt-month">{monthShort[d.getMonth()]}</span>
                  </div>
                  <div className="portal-appt-info">
                    <div className="portal-appt-label">Nächste Sitzung</div>
                    <div className="portal-appt-title">Coaching-Sitzung #{nextSession.session_number || '\u2014'}</div>
                    <div className="portal-appt-meta">
                      <span><Clock size={12} /> {fmtTime(nextSession.scheduled_at)}</span>
                    </div>
                  </div>
                  <div className="portal-appt-actions">
                    {(new Date(nextSession.scheduled_at).getTime() - Date.now()) / (1000 * 60 * 60) > 24 && (
                      <button className="portal-btn-sm portal-btn-outline"
                        onClick={() => handleCancelSession(nextSession.id)}
                        disabled={cancelLoading === nextSession.id}>
                        <XCircle size={14} /> Absagen
                      </button>
                    )}
                  </div>
                </div>
              );
            })() : (
              <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                Kein anstehender Termin geplant
              </div>
            )}
          </div>
        </div>

        {/* Coaching Progress */}
        <div className="portal-section-box">
          <div className="portal-section-header">
            <span className="portal-section-title"><BarChart2 size={16} /> Ihr Coaching-Fortschritt</span>
          </div>
          <div className="portal-section-body">
            <div className="portal-journey-stats">
              <div className="portal-journey-stat">
                <div className="portal-journey-value">{completedSessions.length}</div>
                <div className="portal-journey-label">Sitzungen</div>
              </div>
              <div className="portal-journey-stat">
                <div className="portal-journey-value">{homework.length - openHw.length}</div>
                <div className="portal-journey-label">Aufgaben erledigt</div>
              </div>
              <div className="portal-journey-stat">
                <div className="portal-journey-value">{documents.length}</div>
                <div className="portal-journey-label">Dokumente</div>
              </div>
            </div>
            {sessions.length > 0 && (
              <div>
                <div className="portal-progress-label">
                  <span>Sitzungs-Fortschritt</span>
                  <strong>{completedSessions.length} / {sessions.length}</strong>
                </div>
                <div className="portal-progress-bar-bg">
                  <div className="portal-progress-bar-fill"
                    style={{ width: `${sessions.length ? (completedSessions.length / sessions.length) * 100 : 0}%` }} />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Homework (max 3) */}
        {(!settings || settings.show_homework) && (
          <div className="portal-section-box">
            <div className="portal-section-header">
              <span className="portal-section-title"><ClipboardList size={16} /> Offene Aufgaben</span>
              <button className="portal-section-link" onClick={() => navigateTab('sitzungen' as TabKey)}>Alle Hausaufgaben &rarr;</button>
            </div>
            <div className="portal-section-body">
              {openHw.length === 0 ? (
                <div style={{ padding: 12, textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                  <CheckCircle size={20} style={{ marginBottom: 4, opacity: 0.5 }} /><br />
                  Alle Aufgaben erledigt!
                </div>
              ) : (
                <div className="portal-hw-list">
                  {openHw.slice(0, 3).map((hw) => (
                    <div key={hw.session_id} className="portal-hw-item">
                      <button className="portal-hw-check" onClick={() => navigateTab('sitzungen' as TabKey)} />
                      <div className="portal-hw-text">{hw.homework_text.length > 80 ? hw.homework_text.substring(0, 80) + '...' : hw.homework_text}</div>
                      {hw.scheduled_at && <span className="portal-hw-date">{fmtDate(hw.scheduled_at)}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Documents (max 4) */}
        {(!settings || settings.show_documents) && (
          <div className="portal-section-box">
            <div className="portal-section-header">
              <span className="portal-section-title"><FolderOpen size={16} /> Dokumente</span>
              <button className="portal-section-link" onClick={() => navigateTab('sitzungen' as TabKey)}>Alle Dokumente &rarr;</button>
            </div>
            <div className="portal-section-body">
              {documents.length === 0 ? (
                <div style={{ padding: 12, textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem' }}>Keine Dokumente vorhanden</div>
              ) : (
                <div className="portal-doc-list">
                  {documents.slice(0, 4).map((d) => (
                    <div key={d.id} className="portal-doc-item" onClick={() => handleDownloadDoc(d.id, d.file_name)}>
                      <div className={`portal-doc-icon ${d.file_type === 'pdf' || d.file_name.endsWith('.pdf') ? 'portal-doc-icon-pdf' : 'portal-doc-icon-default'}`}>
                        <FileText size={16} />
                      </div>
                      <div className="portal-doc-info">
                        <div className="portal-doc-name">{d.title || d.file_name}</div>
                        <div className="portal-doc-meta">{d.document_type || d.file_type || '\u2014'} · {fmtDate(d.created_at)}</div>
                      </div>
                      <button className="portal-doc-download"><Download size={14} /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

      </div>

      {/* GDPR hint (subtle, full-width) */}
      <div className="portal-gdpr-inline">
        <Shield size={14} />
        <span>Ihre Daten sind DSGVO-konform geschützt.</span>
        <button onClick={() => navigateTab('datenschutz' as TabKey)}>Mehr erfahren</button>
      </div>
    </>
  );

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      {/* Mobile Header */}
      <div className="portal-mobile-header">
        <div>
          <strong style={{ color: 'var(--primary)', fontSize: '1rem' }}>{profile?.coach_business_name || 'CoachDesk365'}</strong>
          <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginLeft: 8 }}>Portal</span>
        </div>
        <button className="mobile-menu-btn" onClick={() => setSidebarOpen(!sidebarOpen)}>
          {sidebarOpen ? <X size={18} /> : <Menu size={18} />} Menü
        </button>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && <div className="mobile-overlay" onClick={() => setSidebarOpen(false)} />}

      <div className="portal-layout">
        {/* SIDEBAR */}
        <aside className={`portal-sidebar ${sidebarOpen ? 'mobile-open' : ''}`}>
          <div className="sidebar-brand">
            <div className="brand-logo-row">
              <LogoIcon size={28} />
              <span className="sidebar-brand-name">{profile?.coach_business_name || 'CoachDesk365'}</span>
            </div>
            <div className="sidebar-brand-sub">Klienten-Portal</div>
          </div>

          <div className="sidebar-profile">
            <div className="portal-avatar">{initials}</div>
            <div className="sidebar-profile-name">{profile.first_name} {profile.last_name}</div>
            <div className="sidebar-profile-email">{profile.email}</div>
          </div>

          <nav className="sidebar-nav">
            {navSections.map((section) => (
              <React.Fragment key={section.label}>
                {section.items.length > 0 && (
                  <>
                    <div className="nav-section-label">{section.label}</div>
                    {section.items.map((item) => (
                      <button
                        key={item.key}
                        className={`portal-nav-item ${tab === item.key ? 'active' : ''}`}
                        onClick={() => { navigateTab(item.key as TabKey); setSidebarOpen(false); }}
                      >
                        <span className="portal-nav-icon">{item.icon}</span>
                        {item.label}
                        {item.badge > 0 && (
                          <span className={`portal-nav-badge ${item.key === 'sitzungen' ? 'portal-nav-badge-danger' : ''}`}>
                            {item.badge}
                          </span>
                        )}
                      </button>
                    ))}
                  </>
                )}
              </React.Fragment>
            ))}
          </nav>

          <div className="sidebar-footer">
            <button className="portal-nav-item portal-nav-logout" onClick={handleLogout}>
              <span className="portal-nav-icon"><LogOut size={16} /></span>
              Abmelden
            </button>
          </div>
        </aside>

        {/* MAIN CONTENT */}
        <main className="portal-main">
          {/* Dashboard View */}
          {tab === ('dashboard' as TabKey) && renderDashboard()}

          {/* Invoices Tab */}
          {tab === 'rechnungen' && (
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <table className="portal-invoices-table">
              <thead>
                <tr><th>Nr.</th><th>Betreff</th><th>Datum</th><th>Fällig</th><th>Betrag</th><th>Bezahlt</th><th>Status</th><th style={{ minWidth: 200 }}>Aktionen</th></tr>
              </thead>
              <tbody>
                {invoices.length === 0 ? (
                  <tr><td colSpan={8} style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)' }}>Keine Rechnungen vorhanden</td></tr>
                ) : invoices.map((inv) => {
                  const isPayable = ['sent', 'overdue', 'partially_paid'].includes(inv.status) && inv.stripe_available && inv.remaining > 0;
                  const statusCls = inv.status === 'paid' ? 'portal-status-paid' : inv.status === 'overdue' ? 'portal-status-overdue' : inv.status === 'partially_paid' ? 'portal-status-partial' : inv.status === 'cancelled' ? 'portal-status-cancelled' : 'portal-status-open';
                  return (
                  <tr key={inv.id}>
                    <td className="invoice-number">{inv.invoice_number}</td>
                    <td>{inv.subject || '\u2014'}</td>
                    <td className="invoice-date">{fmtDate(inv.invoice_date)}</td>
                    <td className="invoice-date">{fmtDate(inv.due_date)}</td>
                    <td className="invoice-amount">{fmt(inv.total_eur)}</td>
                    <td className={inv.total_paid > 0 ? 'portal-paid-amount' : 'text-muted'} style={{ whiteSpace: 'nowrap' }}>
                      {inv.total_paid > 0 ? fmt(inv.total_paid) : '\u2014'}
                    </td>
                    <td>
                      <span className={`portal-status-badge ${statusCls}`}>
                        {statusLabels[inv.status] || inv.status}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {isPayable && (
                          <button className="btn btn-sm btn-accent" onClick={() => setPayInvoice(inv)}>
                            <CreditCard size={14} /> Bezahlen
                          </button>
                        )}
                        <button className="btn btn-sm btn-primary" onClick={() => handlePreview('invoice', inv.id, `Rechnung ${inv.invoice_number}`)}>
                          <Eye size={14} /> Anzeigen
                        </button>
                        <button className="btn btn-sm btn-secondary" onClick={() => handleDownloadPdf(inv.id, inv.invoice_number)}>
                          <Download size={14} /> PDF
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Bookings Tab — Calendar View (Redesign v2) */}
        {tab === 'termine' && (() => {
          const nowDate = new Date();
          const upcomingBookings = bookings.filter((b) => {
            if (b.status === 'cancelled') return false;
            if (b.start_time) return new Date(b.start_time) >= nowDate;
            return true;
          });
          const pastBookings = bookings.filter((b) => {
            if (b.status === 'cancelled') return true;
            if (b.start_time) return new Date(b.start_time) < nowDate;
            return false;
          });

          // Booked dates from bookings (for blue dots)
          const bookedDates = new Set<string>();
          bookings.filter(b => b.start_time && b.status !== 'cancelled').forEach(b => {
            bookedDates.add(b.start_time!.split('T')[0]);
          });

          // Calculate slot duration in minutes
          const getSlotDuration = (slot: PortalSlot) => {
            const start = new Date(slot.start_time);
            const end = new Date(slot.end_time);
            return Math.round((end.getTime() - start.getTime()) / (1000 * 60));
          };

          const currentSlots = selectedDate ? getSlotsForDate(selectedDate) : [];

          return (
          <div className="portal-termine-layout">
            {/* ── LEFT PANEL: Calendar + Meine Buchungen ── */}
            <div className="portal-termine-left">
              {/* Calendar */}
              <div className="portal-termine-calendar">
                <div className="portal-termine-cal-nav">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <h3 className="portal-termine-month">{monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}</h3>
                    <button className="portal-termine-today-btn" onClick={() => setCurrentMonth(new Date())}>Heute</button>
                  </div>
                  <div className="portal-termine-nav-buttons">
                    <button className="portal-termine-nav-btn" onClick={() => { const d = new Date(currentMonth); d.setMonth(d.getMonth() - 1); setCurrentMonth(d); }}>
                      <ChevronLeft size={16} />
                    </button>
                    <button className="portal-termine-nav-btn" onClick={() => { const d = new Date(currentMonth); d.setMonth(d.getMonth() + 1); setCurrentMonth(d); }}>
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>

                <div className="portal-termine-grid">
                  <div className="portal-termine-grid-header">
                    {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map((d) => (
                      <div key={d} className="portal-termine-weekday">{d}</div>
                    ))}
                  </div>
                  <div className="portal-termine-grid-body">
                    {generateMonthGrid().map(({ date: d, isCurrentMonth }) => {
                      const dateStr = toLocalDateStr(d);
                      const hasSlots = availableDates.has(dateStr);
                      const hasBooking = bookedDates.has(dateStr);
                      const isSelected = dateStr === selectedDate;
                      const isPast = dateStr < today;
                      const isToday = dateStr === today;
                      const cls = [
                        'portal-termine-day',
                        !isCurrentMonth && 'other-month',
                        isPast && isCurrentMonth && 'is-past',
                        isToday && 'is-today',
                        isSelected && 'is-selected',
                        hasSlots && !isPast && isCurrentMonth && 'has-slots',
                      ].filter(Boolean).join(' ');
                      return (
                        <button
                          key={dateStr}
                          className={cls}
                          onClick={() => { if (hasSlots && !isPast && isCurrentMonth) { setSelectedDate(dateStr); setSelectedSlot(null); setBookingSuccess(null); } }}
                          disabled={(!hasSlots || isPast) && isCurrentMonth}
                        >
                          <span className="portal-termine-day-num">{d.getDate()}</span>
                          <div className="portal-termine-day-dots">
                            {hasSlots && !isPast && isCurrentMonth && <span className="portal-termine-dot dot-available" />}
                            {hasBooking && isCurrentMonth && <span className="portal-termine-dot dot-booked" />}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Legend */}
                <div className="portal-termine-legend">
                  <div className="portal-termine-legend-item"><span className="portal-termine-legend-dot dot-available" /> Freie Slots</div>
                  <div className="portal-termine-legend-item"><span className="portal-termine-legend-dot dot-booked" /> Gebucht</div>
                  <div className="portal-termine-legend-item"><span className="portal-termine-legend-dot dot-full" /> Ausgebucht</div>
                </div>
              </div>

              {/* Meine Buchungen */}
              <div className="portal-termine-bookings">
                <div className="portal-termine-bookings-header">
                  <span>📋 Meine Buchungen</span>
                  {bookings.length > 0 && <span className="portal-termine-bookings-count">{bookings.length}</span>}
                </div>

                {bookings.length === 0 && (
                  <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.88rem' }}>
                    Noch keine Buchungen vorhanden.
                  </div>
                )}

                {/* Upcoming */}
                {upcomingBookings.length > 0 && (
                  <div>
                    <div className="portal-termine-section-label">Kommende Termine</div>
                    <div className="portal-termine-booking-cards">
                      {upcomingBookings.map((b) => (
                        <div key={b.id} className="portal-termine-booking-card">
                          <div className="portal-termine-booking-datebox">
                            <span className="portal-termine-booking-day">{b.start_time ? new Date(b.start_time).getDate() : '–'}</span>
                            <span className="portal-termine-booking-month">{b.start_time ? monthNames[new Date(b.start_time).getMonth()].slice(0, 3) : ''}</span>
                          </div>
                          <div className="portal-termine-booking-info">
                            <div className="portal-termine-booking-time">
                              {b.start_time ? `${fmtTime(b.start_time)} – ${b.end_time ? fmtTime(b.end_time) + ' ' : ''}Uhr` : '–'}
                            </div>
                            <div className="portal-termine-booking-meta">
                              <span>{b.location_type === 'online' ? 'Online' : b.location_type === 'in_person' ? 'Vor Ort' : b.location_type || '–'}</span>
                              <span>·</span>
                              <span>{b.booking_reference}</span>
                            </div>
                          </div>
                          <span className={`portal-termine-status ${b.status === 'confirmed' ? 'confirmed' : 'pending'}`}>
                            {b.status === 'confirmed' ? '✓ Bestätigt' : statusLabels[b.status] || b.status}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Past bookings — collapsible */}
                {pastBookings.length > 0 && (
                  <details className="portal-termine-past-details">
                    <summary className="portal-termine-past-toggle">
                      ▼ Vergangene & stornierte ({pastBookings.length})
                    </summary>
                    <div className="portal-termine-booking-cards" style={{ marginTop: 10 }}>
                      {pastBookings.map((b) => (
                        <div key={b.id} className="portal-termine-booking-card portal-termine-booking-card--past">
                          <div className="portal-termine-booking-datebox" style={{ background: b.status === 'cancelled' ? 'var(--text-muted)' : undefined }}>
                            <span className="portal-termine-booking-day">{b.start_time ? new Date(b.start_time).getDate() : '–'}</span>
                            <span className="portal-termine-booking-month">{b.start_time ? monthNames[new Date(b.start_time).getMonth()].slice(0, 3) : ''}</span>
                          </div>
                          <div className="portal-termine-booking-info">
                            <div className="portal-termine-booking-time">
                              {b.start_time ? `${fmtTime(b.start_time)} – ${b.end_time ? fmtTime(b.end_time) + ' ' : ''}Uhr` : '–'}
                            </div>
                            <div className="portal-termine-booking-meta">
                              <span>{b.location_type === 'online' ? 'Online' : b.location_type === 'in_person' ? 'Vor Ort' : b.location_type || '–'}</span>
                              <span>·</span>
                              <span>{b.booking_reference}</span>
                            </div>
                          </div>
                          <span className={`portal-termine-status ${b.status === 'confirmed' ? 'confirmed' : 'pending'}`}>
                            {statusLabels[b.status] || b.status}
                          </span>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            </div>

            {/* ── RIGHT PANEL: Slot Booking ── */}
            <div className="portal-termine-booking-panel">
              {/* Empty state — no date selected */}
              {!selectedDate && !bookingSuccess && (
                <div className="portal-termine-empty">
                  <div className="portal-termine-empty-icon">📅</div>
                  <h3>Datum wählen</h3>
                  <p>Wählen Sie im Kalender einen Tag mit grünem Punkt, um freie Zeitfenster anzuzeigen.</p>
                </div>
              )}

              {/* Date selected — slots view */}
              {selectedDate && !bookingSuccess && (
                <>
                  {/* Header with date display */}
                  <div className="portal-termine-panel-header">
                    <div className="portal-termine-date-display">
                      <div className="portal-termine-date-icon">
                        <span className="portal-termine-date-day">{new Date(selectedDate + 'T00:00:00').getDate()}</span>
                        <span className="portal-termine-date-month">{monthNames[new Date(selectedDate + 'T00:00:00').getMonth()].slice(0, 3)}</span>
                      </div>
                      <div className="portal-termine-date-text">
                        <h3>{new Date(selectedDate + 'T00:00:00').toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long' })}</h3>
                        <p>{currentSlots.length} freie Zeitfenster verfügbar</p>
                        <span className="portal-termine-available-badge">
                          <span className="portal-termine-pulse-dot" />
                          Verfügbar
                        </span>
                      </div>
                    </div>
                    <button className="portal-termine-panel-close" onClick={() => { setSelectedDate(null); setSelectedSlot(null); }}>
                      <X size={16} />
                    </button>
                  </div>

                  {/* Slot cards */}
                  <div className="portal-termine-slots">
                    {currentSlots.length === 0 ? (
                      <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.88rem' }}>
                        Keine freien Zeiten an diesem Tag.
                      </div>
                    ) : (
                      currentSlots.map((slot) => {
                        const duration = getSlotDuration(slot);
                        const isChosen = selectedSlot?.id === slot.id;
                        return (
                          <div
                            key={slot.id}
                            className={`portal-termine-slot${isChosen ? ' selected' : ''}`}
                            onClick={() => setSelectedSlot(slot)}
                          >
                            <div className="portal-termine-slot-info">
                              <div className="portal-termine-slot-time">{fmtTime(slot.start_time)} – {fmtTime(slot.end_time)} Uhr</div>
                              <div className="portal-termine-slot-meta">
                                <span className="portal-termine-slot-duration">⏱ {duration} Min.</span>
                                <span className="portal-termine-slot-location-badge">
                                  {slot.location_type === 'online' ? '💻 Online · Zoom' : slot.location_type === 'in_person' ? '📍 Vor Ort' : '📍 Flexibel'}
                                </span>
                              </div>
                            </div>
                            <div className={`portal-termine-slot-check${isChosen ? ' checked' : ''}`}>
                              {isChosen && '✓'}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>

                  {/* Book button */}
                  <div className="portal-termine-panel-footer">
                    <button
                      className="portal-termine-book-btn"
                      disabled={!selectedSlot || bookingLoading}
                      onClick={handleBook}
                    >
                      📅 {bookingLoading ? 'Wird gebucht...' : 'Termin verbindlich buchen'}
                    </button>
                    <div className="portal-termine-book-note">Kostenlose Stornierung bis 24h vor dem Termin</div>
                  </div>
                </>
              )}

              {/* Success state */}
              {bookingSuccess && (
                <div className="portal-termine-success">
                  <div className="portal-cal-success-icon">
                    <CheckCircle size={48} />
                  </div>
                  <h4>Termin gebucht!</h4>
                  <p>Ihr Termin wurde erfolgreich reserviert.</p>
                  <div className="portal-cal-success-ref">
                    Buchungsreferenz: <strong>{bookingSuccess}</strong>
                  </div>
                  <button
                    className="portal-termine-book-btn"
                    style={{ marginTop: 16, width: 'auto', padding: '10px 24px' }}
                    onClick={() => { setBookingSuccess(null); setSelectedDate(null); setSelectedSlot(null); }}
                  >
                    Weitere Termine buchen
                  </button>
                </div>
              )}
            </div>
          </div>
          );
        })()}

        {/* Sessions Tab — Consolidated (Akkordeon + Dokumente + Hausaufgaben) */}
        {tab === 'sitzungen' && (() => {
          // Group sessions by case
          const grouped: Record<string, { name: string; sessions: PortalSession[] }> = {};
          sessions.forEach((s) => {
            const key = s.case_id || 'unknown';
            if (!grouped[key]) grouped[key] = { name: s.case_name || 'Sitzungen', sessions: [] };
            grouped[key].sessions.push(s);
          });
          const caseEntries = Object.entries(grouped);

          const now = Date.now();

          const parseTopics = (topics: string | null): string[] => {
            if (!topics) return [];
            return topics.split(/[,;\n]/).map(t => t.trim()).filter(Boolean);
          };

          const locationLabels: Record<string, string> = { online: 'Online', in_person: 'Vor Ort', phone: 'Telefon' };
          const locationIcons: Record<string, React.ReactNode> = {
            online: <Video size={12} />,
            in_person: <MapPin size={12} />,
            phone: <Clock size={12} />,
          };

          // Documents grouped by case
          const docsByCase: Record<string, PortalDocument[]> = {};
          const docsNoCaseArr: PortalDocument[] = [];
          documents.forEach(d => {
            if (d.case_id) {
              if (!docsByCase[d.case_id]) docsByCase[d.case_id] = [];
              docsByCase[d.case_id].push(d);
            } else {
              docsNoCaseArr.push(d);
            }
          });

          const renderDocItem = (d: PortalDocument) => {
            const isPdf = d.file_type === 'pdf' || d.file_name.endsWith('.pdf');
            return (
              <div key={d.id} className="ps-case-doc-item">
                <div className={`ps-case-doc-icon ${isPdf ? 'pdf' : ''}`}>
                  <FileText size={14} />
                </div>
                <div className="ps-case-doc-name">{d.title || d.file_name}</div>
                <div className="ps-case-doc-meta">{fmtDate(d.created_at)}</div>
                <div className="ps-case-doc-actions">
                  <button onClick={() => handlePreview('document', d.id, d.title || d.file_name, d.file_name)}>
                    <Eye size={12} /> Anzeigen
                  </button>
                  <button onClick={() => handleDownloadDoc(d.id, d.file_name)}>
                    <Download size={12} />
                  </button>
                </div>
              </div>
            );
          };

          return (
            <div>
              {sessions.length === 0 && documents.length === 0 ? (
                <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>Keine Sitzungen vorhanden.</div>
              ) : (
                <>
                  {/* Render per case */}
                  {caseEntries.map(([caseId, group]) => {
                    const caseVisibleSessions = group.sessions.filter(s => s.status !== 'cancelled');
                    const caseNextSession = caseVisibleSessions
                      .filter(s => s.status === 'scheduled' && s.scheduled_at && new Date(s.scheduled_at).getTime() > now)
                      .sort((a, b) => new Date(a.scheduled_at!).getTime() - new Date(b.scheduled_at!).getTime())[0];
                    const caseDocs = docsByCase[caseId] || [];

                    return (
                      <div key={caseId} className="portal-case-group">
                        {/* Case Header */}
                        <div className="portal-case-header">
                          <FolderOpen size={18} style={{ color: 'var(--primary)' }} />
                          <div style={{ flex: 1 }}>
                            <h3 style={{ margin: 0 }}>{group.name}</h3>
                            <span className="text-muted" style={{ fontSize: '0.78rem' }}>
                              {caseVisibleSessions.length} Sitzung{caseVisibleSessions.length !== 1 ? 'en' : ''}
                              {caseDocs.length > 0 && ` · ${caseDocs.length} Dokument${caseDocs.length !== 1 ? 'e' : ''}`}
                            </span>
                          </div>
                        </div>

                        {/* Case Documents */}
                        {(!settings || settings.show_documents) && caseDocs.length > 0 && (
                          <div className="ps-case-docs">
                            <div className="ps-case-docs-header">
                              <FolderOpen size={14} /> Dokumente ({caseDocs.length})
                            </div>
                            {caseDocs.map(renderDocItem)}
                          </div>
                        )}

                        {/* Next Session Banner */}
                        {caseNextSession?.scheduled_at && (
                          <div className="ps-case-next-banner">
                            <Calendar size={14} />
                            <span>
                              Nächste Sitzung: {new Date(caseNextSession.scheduled_at).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' })}{' '}
                              um {new Date(caseNextSession.scheduled_at).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr
                            </span>
                          </div>
                        )}

                        {/* Akkordeon Session Cards */}
                        <div className="portal-case-tree">
                          {caseVisibleSessions.map((s) => {
                            const sStatusCls = s.status === 'completed' ? 'portal-status-completed' : s.status === 'scheduled' ? 'portal-status-scheduled' : 'portal-status-open';
                            const isExpanded = expandedSessions.has(s.id);
                            const topics = parseTopics(s.session_topic);
                            const hasContent = s.session_topic || s.session_results || s.session_agreements || s.session_homework_next;
                            const canCancel = s.status === 'scheduled' && s.scheduled_at && (new Date(s.scheduled_at).getTime() - now) / (1000 * 60 * 60) > 24;

                            return (
                              <div key={s.id}>
                                {/* Collapsed Header */}
                                <div className="ps-accordion-header" onClick={() => toggleSession(s.id)}>
                                  <div className="ps-accordion-header-left">
                                    <span className="portal-session-title">Sitzung {s.session_number || '\u2014'}</span>
                                    {s.scheduled_at && <span className="portal-session-date">{fmtDateTime(s.scheduled_at)}</span>}
                                    {s.location_type && (
                                      <span className="ps-location-indicator">
                                        {locationIcons[s.location_type] || <MapPin size={12} />}
                                        {locationLabels[s.location_type] || s.location_type}
                                      </span>
                                    )}
                                  </div>
                                  <div className="ps-accordion-header-right">
                                    {s.status && (
                                      <span className={`portal-status-badge ${sStatusCls}`}>
                                        {statusLabels[s.status] || s.status}
                                      </span>
                                    )}
                                    <span className={`ps-accordion-chevron ${isExpanded ? 'open' : ''}`}>
                                      <ChevronDown size={16} />
                                    </span>
                                  </div>
                                </div>

                                {/* Expanded Content */}
                                <div className={`ps-accordion-content ${isExpanded ? 'open' : ''}`}>
                                  {isExpanded && (
                                    <>
                                      {/* Cancel button */}
                                      {canCancel && (
                                        <div style={{ marginBottom: 12 }}>
                                          <button
                                            className="btn btn-sm"
                                            onClick={() => handleCancelSession(s.id)}
                                            disabled={cancelLoading === s.id}
                                            style={{ background: 'rgba(var(--danger-rgb, 192, 86, 79), 0.08)', color: 'var(--danger)', border: '1px solid rgba(var(--danger-rgb, 192, 86, 79), 0.2)', display: 'flex', alignItems: 'center', gap: 6 }}
                                          >
                                            <XCircle size={14} /> {cancelLoading === s.id ? 'Wird storniert...' : 'Termin stornieren'}
                                          </button>
                                        </div>
                                      )}
                                      {s.status === 'scheduled' && s.scheduled_at && !canCancel && (
                                        <div style={{ marginBottom: 12, fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                                          Stornierung nicht mehr möglich (weniger als 24 Std. vor dem Termin)
                                        </div>
                                      )}

                                      {/* Topics as Tags */}
                                      {topics.length > 0 && (
                                        <div style={{ marginBottom: 10 }}>
                                          <strong className="text-muted" style={{ fontSize: '0.78rem', display: 'block', marginBottom: 4 }}>Thema</strong>
                                          <div className="ps-session-tags">
                                            {topics.map((t, i) => <span key={i} className="ps-session-tag">{t}</span>)}
                                          </div>
                                        </div>
                                      )}

                                      {/* Results */}
                                      {s.session_results && (
                                        <div className="portal-session-detail" style={{ marginBottom: 10 }}>
                                          <strong>Ergebnisse</strong>
                                          <p style={{ whiteSpace: 'pre-wrap' }}>{s.session_results}</p>
                                        </div>
                                      )}

                                      {/* Agreements */}
                                      {s.session_agreements && (
                                        <div className="portal-session-detail" style={{ marginBottom: 10 }}>
                                          <strong>Vereinbarungen</strong>
                                          <p style={{ whiteSpace: 'pre-wrap' }}>{s.session_agreements}</p>
                                        </div>
                                      )}

                                      {/* Homework with inline submission */}
                                      {(!settings || settings.show_homework) && s.session_homework_next && (
                                        <div className="ps-homework-section">
                                          <div>
                                            <ClipboardList size={15} />
                                            <div>
                                              <strong style={{ fontSize: '0.8rem' }}>Hausaufgabe</strong>
                                              <p style={{ margin: '2px 0 0', whiteSpace: 'pre-wrap' }}>{s.session_homework_next}</p>
                                            </div>
                                          </div>
                                          {s.status !== 'completed' && (
                                            <div className="ps-homework-form">
                                              <textarea
                                                className="ps-homework-textarea"
                                                placeholder="Ihre Antwort..."
                                                value={hwResponses[s.id] || ''}
                                                onChange={(e) => setHwResponses(prev => ({ ...prev, [s.id]: e.target.value }))}
                                              />
                                              <button
                                                className="btn btn-primary btn-sm"
                                                onClick={() => handleSubmitHomework(s.id)}
                                                disabled={hwSubmitting === s.id || !hwResponses[s.id]?.trim()}
                                                style={{ alignSelf: 'flex-end' }}
                                              >
                                                <Send size={14} /> {hwSubmitting === s.id ? '...' : 'Absenden'}
                                              </button>
                                            </div>
                                          )}
                                        </div>
                                      )}

                                      {/* Fallback */}
                                      {!hasContent && (
                                        <p className="text-muted text-sm">
                                          {s.status === 'completed' ? 'Keine Details hinterlegt.' : 'Noch keine Details vorhanden.'}
                                        </p>
                                      )}
                                    </>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}

                  {/* General Documents (no case_id) */}
                  {(!settings || settings.show_documents) && docsNoCaseArr.length > 0 && (
                    <div className="ps-general-docs">
                      <div className="ps-general-docs-header">
                        <FolderOpen size={18} /> Allgemeine Dokumente
                      </div>
                      <div className="ps-case-docs">
                        {docsNoCaseArr.map(renderDocItem)}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })()}

        {/* Follow-ups Tab */}
        {tab === 'followups' && (
          <div>
            {followups.length === 0 ? (
              <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>Keine Follow-ups vorhanden.</div>
            ) : (
              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <table className="table">
                  <thead><tr><th>Datum</th><th>Vorlage</th><th>Status</th><th></th></tr></thead>
                  <tbody>
                    {followups.map((fu) => (
                      <tr key={fu.id}>
                        <td className="text-light">{fu.scheduled_date ? fmtDate(fu.scheduled_date) : '\u2014'}</td>
                        <td>{fu.template_name || '\u2014'}</td>
                        <td><span className="badge badge-new">{statusLabels[fu.status] || fu.status}</span></td>
                        <td>
                          {fu.token && fu.status !== 'beantwortet' && fu.status !== 'abgeschlossen' && (
                            <a
                              href={`/formular/${fu.token}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-sm btn-primary"
                            >
                              <ExternalLink size={14} /> Ausfüllen
                            </a>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Profile Tab */}
        {tab === 'profil' && (
          <div>
            <div className="card" style={{ padding: 24 }}>
              <h3 style={{ margin: '0 0 20px 0' }}>Mein Profil</h3>

              {/* Profile fields with "Ändern" buttons */}
              {(() => {
                const fields: { field: string; label: string; value: string }[] = [
                  { field: 'first_name', label: 'Vorname', value: profile.first_name || '' },
                  { field: 'last_name', label: 'Nachname', value: profile.last_name || '' },
                  { field: 'email', label: 'E-Mail', value: profile.email || '' },
                  { field: 'phone', label: 'Telefon', value: profile.phone || '' },
                ];
                const renderRow = (f: { field: string; label: string; value: string }) => {
                  const pending = changeRequests.find((cr: any) => cr.field_name === f.field && cr.status === 'pending');
                  return (
                    <div key={f.field} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--border-light)' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 2 }}>{f.label}</div>
                        <div style={{ fontSize: '0.95rem' }}>{f.value || <span style={{ color: 'var(--text-light)' }}>{'\u2014'}</span>}</div>
                      </div>
                      {pending ? (
                        <span style={{ fontSize: '0.75rem', color: 'var(--warning, #D4A853)', background: 'rgba(var(--warning-rgb, 212, 168, 67), 0.1)', padding: '3px 10px', borderRadius: 12 }}>
                          Antrag gestellt
                        </span>
                      ) : (
                        <button
                          className="btn btn-secondary"
                          style={{ fontSize: '0.75rem', padding: '4px 10px', whiteSpace: 'nowrap' }}
                          onClick={() => {
                            setChangeModal({ field: f.field, label: f.label, currentValue: f.value });
                            setChangeNewValue('');
                            setChangeReason('');
                          }}
                        >
                          Ändern
                        </button>
                      )}
                    </div>
                  );
                };
                return (
                  <>
                    {fields.map(renderRow)}
                    {/* Adresse als zusammengefasste Zeile */}
                    {(() => {
                      const addrPending = changeRequests.find((cr: any) => cr.field_name === 'address' && cr.status === 'pending');
                      const addrParts = [profile.street, [profile.zip_code, profile.city].filter(Boolean).join(' '), profile.country].filter(Boolean);
                      const addrDisplay = addrParts.length > 0 ? addrParts.join(', ') : '';
                      return (
                        <div key="address" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--border-light)' }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 2 }}>Adresse</div>
                            <div style={{ fontSize: '0.95rem' }}>{addrDisplay || <span style={{ color: 'var(--text-light)' }}>{'\u2014'}</span>}</div>
                          </div>
                          {addrPending ? (
                            <span style={{ fontSize: '0.75rem', color: 'var(--warning, #D4A853)', background: 'rgba(var(--warning-rgb, 212, 168, 67), 0.1)', padding: '3px 10px', borderRadius: 12 }}>
                              Antrag gestellt
                            </span>
                          ) : (
                            <button
                              className="btn btn-secondary"
                              style={{ fontSize: '0.75rem', padding: '4px 10px', whiteSpace: 'nowrap' }}
                              onClick={() => {
                                setAddressForm({
                                  street: profile.street || '',
                                  zip_code: profile.zip_code || '',
                                  city: profile.city || '',
                                  country: profile.country || '',
                                });
                                setChangeModal({ field: 'address', label: 'Adresse', currentValue: addrDisplay });
                                setChangeReason('');
                              }}
                            >
                              Ändern
                            </button>
                          )}
                        </div>
                      );
                    })()}
                  </>
                );
              })()}

              {profile.tax_number && (
                <div style={{ padding: '10px 0' }}>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 2 }}>Steuernummer</div>
                  <div style={{ fontSize: '0.95rem' }}>{profile.tax_number}</div>
                </div>
              )}
            </div>

            {/* Offene Änderungsanträge */}
            {changeRequests.length > 0 && (
              <div className="card" style={{ padding: 24, marginTop: 16 }}>
                <h4 style={{ margin: '0 0 16px 0', fontSize: '0.95rem' }}>Meine Änderungsanträge</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {changeRequests.map((cr: any) => {
                    const fieldLabels: Record<string, string> = {
                      first_name: 'Vorname', last_name: 'Nachname', email: 'E-Mail', phone: 'Telefon',
                      address: 'Adresse', street: 'Straße', zip_code: 'PLZ', city: 'Ort', country: 'Land',
                    };
                    const formatAddrValue = (val: string) => {
                      try {
                        const a = JSON.parse(val);
                        return [a.street, [a.zip_code, a.city].filter(Boolean).join(' '), a.country].filter(Boolean).join(', ');
                      } catch { return val; }
                    };
                    const statusStyle: Record<string, { bg: string; color: string; text: string }> = {
                      pending: { bg: 'rgba(var(--warning-rgb, 212, 168, 67), 0.1)', color: 'var(--warning, #D4A853)', text: 'Warten auf Prüfung' },
                      approved: { bg: 'rgba(var(--success-rgb, 91, 140, 90), 0.1)', color: 'var(--success, #5B8C5A)', text: 'Genehmigt' },
                      rejected: { bg: 'rgba(var(--danger-rgb, 192, 86, 79), 0.08)', color: 'var(--danger, #C0564F)', text: 'Abgelehnt' },
                    };
                    const st = statusStyle[cr.status] || statusStyle.pending;
                    return (
                      <div key={cr.id} style={{ padding: 12, borderRadius: 8, border: '1px solid var(--border-light)', background: 'var(--bg-light)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                          <strong style={{ fontSize: '0.9rem' }}>{fieldLabels[cr.field_name] || cr.field_name}</strong>
                          <span style={{ fontSize: '0.75rem', padding: '2px 8px', borderRadius: 10, background: st.bg, color: st.color }}>
                            {st.text}
                          </span>
                        </div>
                        <div style={{ fontSize: '0.85rem', color: 'var(--text-light)' }}>
                          <span style={{ textDecoration: 'line-through', marginRight: 8 }}>{cr.field_name === 'address' ? formatAddrValue(cr.old_value || '') || '\u2014' : (cr.old_value || '\u2014')}</span>
                          {'\u2192'} <strong>{cr.field_name === 'address' ? formatAddrValue(cr.new_value) : cr.new_value}</strong>
                        </div>
                        {cr.reason && <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 4 }}>Begründung: {cr.reason}</div>}
                        {cr.coach_comment && <div style={{ fontSize: '0.8rem', color: 'var(--danger, #C0564F)', marginTop: 4 }}>Kommentar: {cr.coach_comment}</div>}
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>
                          {new Date(cr.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Modal: Änderung beantragen */}
            {changeModal && (
              <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
                onClick={() => !changeSubmitting && setChangeModal(null)}>
                <div style={{ background: 'var(--bg-card, #fff)', borderRadius: 12, padding: 28, maxWidth: 440, width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}
                  onClick={(e) => e.stopPropagation()}>
                  <h3 style={{ margin: '0 0 20px 0', fontSize: '1.1rem' }}>Änderung beantragen</h3>

                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: 4 }}>Feld</div>
                    <div style={{ fontWeight: 600 }}>{changeModal.label}</div>
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: 4 }}>Aktueller Wert</div>
                    <div style={{ padding: '8px 12px', background: 'var(--bg)', borderRadius: 6 }}>{changeModal.currentValue || '\u2014'}</div>
                  </div>

                  {changeModal.field === 'address' ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 }}>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 4, display: 'block' }}>Straße</label>
                        <input type="text" value={addressForm.street} onChange={(e) => setAddressForm({ ...addressForm, street: e.target.value })} style={{ width: '100%' }} placeholder="Straße und Hausnummer" autoFocus />
                      </div>
                      <div style={{ display: 'flex', gap: 12 }}>
                        <div className="form-group" style={{ margin: 0, flex: '0 0 100px' }}>
                          <label style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 4, display: 'block' }}>PLZ</label>
                          <input type="text" value={addressForm.zip_code} onChange={(e) => setAddressForm({ ...addressForm, zip_code: e.target.value })} style={{ width: '100%' }} placeholder="PLZ" />
                        </div>
                        <div className="form-group" style={{ margin: 0, flex: 1 }}>
                          <label style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 4, display: 'block' }}>Ort</label>
                          <input type="text" value={addressForm.city} onChange={(e) => setAddressForm({ ...addressForm, city: e.target.value })} style={{ width: '100%' }} placeholder="Ort" />
                        </div>
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 4, display: 'block' }}>Land</label>
                        <input type="text" value={addressForm.country} onChange={(e) => setAddressForm({ ...addressForm, country: e.target.value })} style={{ width: '100%' }} placeholder="Land" />
                      </div>
                    </div>
                  ) : (
                    <div className="form-group" style={{ margin: '0 0 16px 0' }}>
                      <label style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 4, display: 'block' }}>Neuer Wert</label>
                      <input
                        type="text"
                        value={changeNewValue}
                        onChange={(e) => setChangeNewValue(e.target.value)}
                        style={{ width: '100%' }}
                        placeholder={`Neuen Wert eingeben...`}
                        autoFocus
                      />
                    </div>
                  )}

                  <div className="form-group" style={{ margin: '0 0 20px 0' }}>
                    <label style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 4, display: 'block' }}>Begründung <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional)</span></label>
                    <textarea
                      value={changeReason}
                      onChange={(e) => setChangeReason(e.target.value)}
                      style={{ width: '100%', minHeight: 60, resize: 'vertical' }}
                      placeholder="z.B. Umzug, Namensänderung..."
                    />
                  </div>

                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button
                      className="btn btn-secondary"
                      onClick={() => setChangeModal(null)}
                      disabled={changeSubmitting}
                    >
                      Abbrechen
                    </button>
                    <button
                      className="btn btn-primary"
                      disabled={changeSubmitting || (changeModal.field === 'address'
                        ? !addressForm.street.trim() && !addressForm.city.trim()
                        : !changeNewValue.trim())}
                      onClick={async () => {
                        setChangeSubmitting(true);
                        try {
                          const newVal = changeModal.field === 'address'
                            ? JSON.stringify(addressForm)
                            : changeNewValue.trim();
                          await portalClientApi.createChangeRequest({
                            field_name: changeModal.field,
                            new_value: newVal,
                            reason: changeReason.trim() || undefined,
                          });
                          showToast('Änderungsantrag eingereicht', 'success');
                          setChangeModal(null);
                          // Refresh change requests
                          try {
                            const crRes = await portalClientApi.changeRequests();
                            setChangeRequests(crRes.data);
                          } catch {}
                        } catch (err: any) {
                          const msg = err?.response?.data?.detail || 'Fehler beim Einreichen';
                          showToast(msg, 'error');
                        } finally {
                          setChangeSubmitting(false);
                        }
                      }}
                    >
                      {changeSubmitting ? 'Wird eingereicht...' : 'Antrag senden'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Nachrichten / Chat Tab */}
        {tab === 'nachrichten' && (
          <div className="card" style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 200px)', padding: 0, overflow: 'hidden' }}>
            {/* Header */}
            <div style={{ padding: '1rem 1.2rem', borderBottom: '1px solid var(--border, #E5E7EB)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <MessageSquare size={18} />
              <h3 style={{ margin: 0, fontSize: '1rem' }}>Nachrichten</h3>
            </div>

            {/* Messages */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {chatMessages.length === 0 && (
                <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '2rem', fontStyle: 'italic' }}>
                  Noch keine Nachrichten. Schreiben Sie die erste Nachricht an Ihren Coach!
                </div>
              )}
              {chatMessages.map((msg: any) => (
                <div
                  key={msg.id}
                  style={{
                    alignSelf: msg.sender_type === 'client' ? 'flex-end' : msg.sender_type === 'system' ? 'center' : 'flex-start',
                    maxWidth: msg.sender_type === 'system' ? '85%' : '70%',
                    padding: '0.6rem 0.9rem',
                    borderRadius: msg.sender_type === 'client' ? '14px 14px 4px 14px' : msg.sender_type === 'system' ? '10px' : '14px 14px 14px 4px',
                    background: msg.sender_type === 'client' ? 'var(--primary, #6366F1)' : msg.sender_type === 'system' ? 'var(--bg, #F8F9FB)' : '#F3F4F6',
                    color: msg.sender_type === 'client' ? 'white' : msg.sender_type === 'system' ? 'var(--text-muted)' : 'var(--text, #1A1D23)',
                    fontSize: msg.sender_type === 'system' ? '0.78rem' : '0.85rem',
                    fontStyle: msg.sender_type === 'system' ? 'italic' : 'normal',
                    border: msg.sender_type === 'system' ? '1px solid var(--border, #E5E7EB)' : 'none',
                    lineHeight: 1.5,
                  }}
                >
                  <div>{msg.content}</div>
                  <div style={{ fontSize: '0.65rem', opacity: 0.7, textAlign: 'right', marginTop: '0.15rem' }}>
                    {msg.created_at ? new Date(msg.created_at).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) : ''}
                  </div>
                </div>
              ))}
            </div>

            {/* Input */}
            <div style={{ display: 'flex', gap: '0.5rem', padding: '0.8rem 1rem', borderTop: '1px solid var(--border, #E5E7EB)', background: 'var(--bg-card, #fff)' }}>
              <input
                type="text"
                placeholder="Nachricht eingeben..."
                value={chatInput}
                onChange={e => setChatInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && chatInput.trim() && !chatLoading) {
                    setChatLoading(true);
                    portalClientApi.sendChatMessage({ content: chatInput.trim() })
                      .then(() => {
                        setChatInput('');
                        return portalClientApi.chatMessages();
                      })
                      .then(res => setChatMessages(res.data.items || []))
                      .catch(() => showToast('Nachricht konnte nicht gesendet werden', 'error'))
                      .finally(() => setChatLoading(false));
                  }
                }}
                style={{ flex: 1, border: '1px solid var(--border, #E5E7EB)', borderRadius: '10px', padding: '0.55rem 0.9rem', fontSize: '0.88rem', outline: 'none' }}
              />
              <button
                disabled={!chatInput.trim() || chatLoading}
                onClick={() => {
                  if (!chatInput.trim() || chatLoading) return;
                  setChatLoading(true);
                  portalClientApi.sendChatMessage({ content: chatInput.trim() })
                    .then(() => {
                      setChatInput('');
                      return portalClientApi.chatMessages();
                    })
                    .then(res => setChatMessages(res.data.items || []))
                    .catch(() => showToast('Nachricht konnte nicht gesendet werden', 'error'))
                    .finally(() => setChatLoading(false));
                }}
                style={{ width: 38, height: 38, borderRadius: '50%', border: 'none', background: 'var(--primary, #6366F1)', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, opacity: !chatInput.trim() || chatLoading ? 0.5 : 1 }}
              >
                <Send size={16} />
              </button>
            </div>
          </div>
        )}

        {/* Hilfe / Support Tab */}
        {tab === 'hilfe' && (
          <div>
            {/* FAQ Section */}
            {supportView === 'list' && (
              <div style={{ marginBottom: 28 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                  <HelpCircle size={20} style={{ color: 'var(--primary)' }} />
                  <h3 style={{ margin: 0 }}>Häufige Fragen</h3>
                </div>
                {[
                  {
                    q: 'Wie kann ich einen Termin buchen?',
                    a: 'Gehen Sie zum Tab „Termine". Dort sehen Sie verfügbare Zeitfenster Ihres Coaches. Wählen Sie einen passenden Termin und klicken Sie auf „Buchen". Sie erhalten anschließend eine Bestätigung.',
                  },
                  {
                    q: 'Wo finde ich meine Rechnungen?',
                    a: 'Unter dem Tab „Rechnungen" finden Sie alle Ihre Rechnungen. Sie können Rechnungen als PDF herunterladen und — sofern Ihr Coach Online-Zahlung anbietet — direkt online bezahlen.',
                  },
                  {
                    q: 'Wie ändere ich meine Profildaten?',
                    a: 'Klicken Sie auf „Profil" in der Navigation. Dort können Sie Ihre Kontaktdaten, Adresse und weitere Angaben aktualisieren. Klicken Sie auf „Änderung beantragen", um die Änderungen Ihrem Coach zur Freigabe zu senden.',
                  },
                  {
                    q: 'Was passiert mit meinen Daten? (DSGVO)',
                    a: 'Ihre Daten werden gemäß der DSGVO verarbeitet und gespeichert. Unter dem Tab „Datenschutz" können Sie Ihre Daten exportieren oder eine vollständige Löschung beantragen. Ihr Coach hat keinen Zugriff auf Ihre Login-Daten.',
                  },
                  {
                    q: 'Wie kann ich mein Passwort ändern?',
                    a: 'Gehen Sie zu „Profil" und scrollen Sie nach unten zum Bereich „Sicherheit". Dort können Sie ein neues Passwort vergeben. Das Passwort muss mindestens 8 Zeichen lang sein.',
                  },
                  {
                    q: 'Wie erreiche ich meinen Coach?',
                    a: 'Unter „Nachrichten" können Sie Ihrem Coach direkt schreiben. Alternativ können Sie unten auf dieser Seite eine Support-Anfrage erstellen, die Ihr Coach per E-Mail erhält.',
                  },
                  {
                    q: 'Ich habe ein technisches Problem — was tun?',
                    a: 'Erstellen Sie unten eine neue Anfrage mit der Kategorie „Technisch". Beschreiben Sie das Problem möglichst genau. Ihr Coach wird sich schnellstmöglich bei Ihnen melden.',
                  },
                ].map((faq, i) => (
                  <details key={i} style={{ marginBottom: 6 }}>
                    <summary style={{
                      cursor: 'pointer', padding: '14px 18px', background: 'var(--bg-card, #fff)',
                      border: '1px solid var(--border, #E5E7EB)', borderRadius: 10,
                      fontWeight: 600, fontSize: '0.9rem', color: 'var(--text)',
                      listStyle: 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    }}>
                      {faq.q}
                      <ChevronDown size={16} style={{ color: 'var(--text-muted)', flexShrink: 0, marginLeft: 8 }} />
                    </summary>
                    <div style={{
                      padding: '12px 18px', fontSize: '0.85rem', lineHeight: 1.6,
                      color: 'var(--text-light, #6B7280)', borderLeft: '3px solid var(--primary)',
                      marginLeft: 18, marginTop: 4, marginBottom: 4,
                    }}>
                      {faq.a}
                    </div>
                  </details>
                ))}
              </div>
            )}

            {/* Support Tickets */}
            {supportView === 'list' && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <h3 style={{ margin: 0 }}>Meine Anfragen</h3>
                  <button className="btn btn-primary" style={{ fontSize: '0.85rem', padding: '8px 16px' }}
                    onClick={() => { setSupportView('create'); setSupportForm({ subject: '', message: '', category: 'sonstiges' }); }}>
                    <Plus size={14} /> Neue Anfrage
                  </button>
                </div>
                {supportTickets.length === 0 ? (
                  <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>
                    Noch keine Anfragen vorhanden.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {supportTickets.map((t) => {
                      const timeAgoStr = t.created_at ? (() => {
                        const diff = Date.now() - new Date(t.created_at!).getTime();
                        const mins = Math.floor(diff / 60000);
                        if (mins < 60) return `vor ${mins} Min.`;
                        const hours = Math.floor(mins / 60);
                        if (hours < 24) return `vor ${hours} Std.`;
                        const days = Math.floor(hours / 24);
                        return `vor ${days} Tag${days > 1 ? 'en' : ''}`;
                      })() : '';

                      return (
                        <div key={t.id} className="card" style={{ padding: '16px 20px', cursor: 'pointer', transition: 'box-shadow 0.15s' }}
                          onClick={async () => {
                            try {
                              const res = await portalClientApi.supportTicketDetail(t.id);
                              setSupportDetail(res.data);
                              setSupportView('detail');
                              setSupportReply('');
                            } catch { showToast('Fehler beim Laden', 'error'); }
                          }}
                          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)'; }}
                          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.boxShadow = ''; }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <div style={{ width: 3, height: 28, borderRadius: 2, background: t.status === 'resolved' ? 'var(--text-muted, #94a3b8)' : 'var(--primary)' }} />
                                <div>
                                  <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>{t.subject}</div>
                                  {t.message && (
                                    <div className="text-muted" style={{ fontSize: '0.8rem', marginTop: 2 }}>
                                      {t.message.length > 80 ? t.message.substring(0, 80) + '...' : t.message}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="text-muted" style={{ fontSize: '0.75rem', marginTop: 6, marginLeft: 11, display: 'flex', gap: 10, alignItems: 'center' }}>
                                <span>{timeAgoStr}</span>
                                {!t.first_response_at && t.status !== 'resolved' && (
                                  <span style={{ color: 'var(--warning, #D4A853)', fontWeight: 500 }}>Warten auf Antwort</span>
                                )}
                                {t.first_response_at && t.status !== 'resolved' && (
                                  <span style={{ color: 'var(--success, #5B8C5A)', fontWeight: 500 }}>Beantwortet</span>
                                )}
                                {(t.reply_count != null && t.reply_count > 0) && (
                                  <span>{t.reply_count} Nachricht{t.reply_count !== 1 ? 'en' : ''}</span>
                                )}
                              </div>
                            </div>
                            <span className={`badge ${t.status === 'open' ? 'badge-new' : t.status === 'resolved' ? 'badge-complete' : 'badge-progress'}`}>
                              {statusLabels[t.status] || t.status}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}

            {supportView === 'create' && (
              <div>
                <button className="btn btn-secondary" style={{ marginBottom: 16, fontSize: '0.85rem' }}
                  onClick={() => setSupportView('list')}>
                  <ArrowLeft size={14} /> Zurück
                </button>
                <div className="card" style={{ padding: 24 }}>
                  <h3 style={{ marginBottom: 16 }}>Neue Anfrage</h3>
                  <form onSubmit={async (e) => {
                    e.preventDefault();
                    if (!supportForm.subject.trim() || !supportForm.message.trim()) return;
                    setSupportLoading(true);
                    try {
                      await portalClientApi.createSupportTicket(supportForm);
                      showToast('Anfrage gesendet!', 'success');
                      setSupportView('list');
                      const res = await portalClientApi.supportTickets();
                      setSupportTickets(res.data);
                    } catch (err: any) {
                      showToast(err.response?.data?.detail || 'Fehler beim Senden', 'error');
                    } finally { setSupportLoading(false); }
                  }}>
                    <div style={{ marginBottom: 16 }}>
                      <label style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 8, display: 'block' }}>Kategorie</label>
                      <div className="category-chips">
                        {[
                          { key: 'technisch', label: 'Technisch' },
                          { key: 'abrechnung', label: 'Abrechnung' },
                          { key: 'termine', label: 'Termine' },
                          { key: 'konto', label: 'Konto' },
                          { key: 'sonstiges', label: 'Sonstiges' },
                        ].map((c) => (
                          <button type="button" key={c.key}
                            className={`chip ${supportForm.category === c.key ? 'chip-active' : ''}`}
                            onClick={() => setSupportForm({ ...supportForm, category: c.key })}>
                            {c.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Betreff</label>
                      <input type="text" value={supportForm.subject}
                        onChange={(e) => setSupportForm({ ...supportForm, subject: e.target.value })}
                        placeholder="Kurze Beschreibung Ihres Anliegens" required />
                    </div>
                    <div className="form-group">
                      <label>Nachricht</label>
                      <textarea rows={5} value={supportForm.message}
                        onChange={(e) => setSupportForm({ ...supportForm, message: e.target.value })}
                        placeholder="Beschreiben Sie Ihr Anliegen..." required />
                    </div>
                    <button type="submit" className="btn btn-primary" disabled={supportLoading}>
                      {supportLoading ? 'Wird gesendet...' : 'Anfrage senden'}
                    </button>
                  </form>
                </div>
              </div>
            )}

            {supportView === 'detail' && supportDetail && (
              <div>
                <button className="btn btn-secondary" style={{ marginBottom: 16, fontSize: '0.85rem' }}
                  onClick={() => { setSupportView('list'); setSupportDetail(null); }}>
                  <ArrowLeft size={14} /> Zurück
                </button>
                <div className="card" style={{ padding: 24 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <h3 style={{ margin: 0 }}>{supportDetail.subject}</h3>
                    <span className={`badge ${supportDetail.status === 'open' ? 'badge-new' : supportDetail.status === 'resolved' ? 'badge-complete' : 'badge-progress'}`}>
                      {statusLabels[supportDetail.status] || supportDetail.status}
                    </span>
                  </div>

                  {/* SLA Info */}
                  {supportDetail.sla_due_at && !supportDetail.first_response_at && supportDetail.status !== 'resolved' && (
                    <div style={{
                      padding: '10px 14px', borderRadius: 8, marginBottom: 12, fontSize: '0.85rem',
                      background: new Date(supportDetail.sla_due_at) < new Date() ? 'rgba(var(--danger-rgb, 192, 86, 79), 0.08)' : 'rgba(var(--success-rgb, 91, 140, 90), 0.08)',
                      border: `1px solid ${new Date(supportDetail.sla_due_at) < new Date() ? 'var(--danger, #C0564F)' : 'var(--success, #5B8C5A)'}`,
                      color: new Date(supportDetail.sla_due_at) < new Date() ? 'var(--danger, #C0564F)' : 'var(--success, #5B8C5A)',
                      display: 'flex', alignItems: 'center', gap: 8,
                    }}>
                      <Clock size={14} />
                      {new Date(supportDetail.sla_due_at) < new Date()
                        ? 'Die Antwortfrist ist überschritten. Ihr Coach wird sich schnellstmöglich melden.'
                        : `Antwort erwartet bis ${fmtDateTime(supportDetail.sla_due_at)}`}
                    </div>
                  )}

                  {/* Original message */}
                  <div style={{ padding: '14px 16px', background: 'var(--primary-light, #faf0ee)', borderRadius: 8, marginBottom: 16, borderLeft: '3px solid var(--primary)' }}>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 6 }}>
                      Ihre Anfrage {supportDetail.created_at ? `vom ${fmtDateTime(supportDetail.created_at)}` : ''}
                    </div>
                    <div style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}>{supportDetail.message}</div>
                  </div>

                  {/* Replies */}
                  {supportDetail.replies.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
                      {supportDetail.replies.map((r) => (
                        <div key={r.id} style={{
                          padding: '12px 16px', borderRadius: 8, fontSize: '0.9rem',
                          background: r.sender_type === 'client' ? 'var(--primary-light, #faf0ee)' : 'var(--bg)',
                          borderLeft: r.sender_type === 'client' ? '3px solid var(--primary)' : '3px solid var(--border)',
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                            <span style={{ fontWeight: 600 }}>{r.sender_name}</span>
                            <span>{r.created_at ? fmtDateTime(r.created_at) : ''}</span>
                          </div>
                          <div style={{ whiteSpace: 'pre-wrap' }}>{r.message}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Reply form */}
                  {supportDetail.status !== 'resolved' && (
                    <form onSubmit={async (e) => {
                      e.preventDefault();
                      if (!supportReply.trim()) return;
                      setSupportLoading(true);
                      try {
                        await portalClientApi.replySupportTicket(supportDetail.id, { message: supportReply });
                        setSupportReply('');
                        const res = await portalClientApi.supportTicketDetail(supportDetail.id);
                        setSupportDetail(res.data);
                        showToast('Antwort gesendet', 'success');
                      } catch (err: any) {
                        showToast(err.response?.data?.detail || 'Fehler', 'error');
                      } finally { setSupportLoading(false); }
                    }}>
                      <div className="form-group">
                        <textarea rows={3} value={supportReply}
                          onChange={(e) => setSupportReply(e.target.value)}
                          placeholder="Ihre Antwort..." required />
                      </div>
                      <button type="submit" className="btn btn-primary" style={{ fontSize: '0.85rem' }} disabled={supportLoading}>
                        <Send size={14} /> {supportLoading ? 'Wird gesendet...' : 'Antwort senden'}
                      </button>
                    </form>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Datenschutz Tab */}
        {tab === 'datenschutz' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div className="card" style={{ padding: 24 }}>
              <h3 style={{ marginBottom: 16 }}>Datenschutz & DSGVO</h3>

              {/* Einwilligungen */}
              <div style={{ marginBottom: 20 }}>
                <h4 style={{ fontSize: '0.95rem', marginBottom: 12 }}>Ihre Einwilligungen</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{
                      display: 'inline-block', width: 10, height: 10, borderRadius: '50%',
                      background: profile.consent_gdpr ? 'var(--success, #5B8C5A)' : 'var(--danger, #C0564F)',
                    }} />
                    <span><strong>DSGVO-Einwilligung:</strong> {profile.consent_gdpr ? 'Erteilt' : 'Nicht erteilt'}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{
                      display: 'inline-block', width: 10, height: 10, borderRadius: '50%',
                      background: profile.consent_processing ? 'var(--success, #5B8C5A)' : 'var(--danger, #C0564F)',
                    }} />
                    <span><strong>Verarbeitungseinwilligung:</strong> {profile.consent_processing ? 'Erteilt' : 'Nicht erteilt'}</span>
                  </div>
                </div>
              </div>

              {/* Ihre Rechte */}
              <div style={{ marginBottom: 20, paddingTop: 16, borderTop: '1px solid var(--border-light)' }}>
                <h4 style={{ fontSize: '0.95rem', marginBottom: 12 }}>Ihre Rechte</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ padding: '10px 14px', background: 'var(--bg)', borderRadius: 8, fontSize: '0.9rem' }}>
                    <strong>Recht auf Auskunft</strong> <span className="text-muted text-sm">(Art. 15 DSGVO)</span>
                    <div className="text-muted" style={{ marginTop: 4, fontSize: '0.85rem' }}>
                      Sie haben das Recht, Auskunft über Ihre gespeicherten personenbezogenen Daten zu erhalten.
                    </div>
                  </div>
                  <div style={{ padding: '10px 14px', background: 'var(--bg)', borderRadius: 8, fontSize: '0.9rem' }}>
                    <strong>Recht auf Berichtigung</strong> <span className="text-muted text-sm">(Art. 16 DSGVO)</span>
                    <div className="text-muted" style={{ marginTop: 4, fontSize: '0.85rem' }}>
                      Sie haben das Recht, unrichtige personenbezogene Daten berichtigen zu lassen.
                    </div>
                  </div>
                  <div style={{ padding: '10px 14px', background: 'var(--bg)', borderRadius: 8, fontSize: '0.9rem' }}>
                    <strong>Recht auf Löschung</strong> <span className="text-muted text-sm">(Art. 17 DSGVO)</span>
                    <div className="text-muted" style={{ marginTop: 4, fontSize: '0.85rem' }}>
                      Sie haben das Recht, die Löschung Ihrer personenbezogenen Daten zu verlangen.
                    </div>
                  </div>
                  <div style={{ padding: '10px 14px', background: 'var(--bg)', borderRadius: 8, fontSize: '0.9rem' }}>
                    <strong>Recht auf Datenübertragbarkeit</strong> <span className="text-muted text-sm">(Art. 20 DSGVO)</span>
                    <div className="text-muted" style={{ marginTop: 4, fontSize: '0.85rem' }}>
                      Sie haben das Recht, Ihre Daten in einem gängigen Format zu erhalten.
                    </div>
                  </div>
                </div>
              </div>

              {/* Aktionen */}
              <div style={{ paddingTop: 16, borderTop: '1px solid var(--border-light)', display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', background: 'var(--bg)', borderRadius: 8 }}>
                  <div>
                    <strong style={{ fontSize: '0.9rem' }}>Datenexport anfordern</strong>
                    <div className="text-muted" style={{ fontSize: '0.8rem', marginTop: 2 }}>Erhalten Sie eine Kopie aller gespeicherten Daten.</div>
                  </div>
                  <button
                    className="btn btn-secondary"
                    style={{ fontSize: '0.8rem', padding: '6px 14px', whiteSpace: 'nowrap' }}
                    onClick={() => showToast('Anfrage wurde an Ihren Coach weitergeleitet', 'success')}
                  >
                    <Download size={14} /> Anfordern
                  </button>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', background: 'var(--bg)', borderRadius: 8 }}>
                  <div>
                    <strong style={{ fontSize: '0.9rem', color: 'var(--danger, #C0564F)' }}>Kontolöschung anfordern</strong>
                    <div className="text-muted" style={{ fontSize: '0.8rem', marginTop: 2 }}>Ihr Portal-Zugang und alle Daten werden gelöscht.</div>
                  </div>
                  <button
                    className="btn"
                    style={{ fontSize: '0.8rem', padding: '6px 14px', whiteSpace: 'nowrap', background: 'rgba(var(--danger-rgb, 192, 86, 79), 0.1)', color: 'var(--danger, #C0564F)', border: '1px solid var(--danger, #C0564F)' }}
                    onClick={() => {
                      if (window.confirm('Möchten Sie wirklich die Löschung Ihres Kontos anfordern? Diese Aktion kann nicht rückgängig gemacht werden.')) {
                        showToast('Löschungsanfrage wurde an Ihren Coach weitergeleitet', 'success');
                      }
                    }}
                  >
                    Anfordern
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Stripe Payment Modal */}
        {payInvoice && (
          <StripeCheckout
            invoice={{
              id: payInvoice.id,
              invoice_number: payInvoice.invoice_number,
              total_eur: payInvoice.total_eur,
              total_paid: payInvoice.total_paid,
            }}
            onClose={() => setPayInvoice(null)}
            onSuccess={() => {
              setPayInvoice(null);
              loadContent();
            }}
            showToast={showToast}
            createIntentFn={(invoiceId) => portalClientApi.createPaymentIntent(invoiceId)}
          />
        )}

        {/* PDF/Dokument-Vorschau Modal */}
        {(previewUrl || previewHtml) && (
          <div className="portal-preview-overlay">
            <div className="portal-preview-header">
              <strong>{previewTitle}</strong>
              <div style={{ display: 'flex', gap: 8 }}>
                {previewUrl && (
                  <a href={previewUrl} download={`${previewTitle}.pdf`} className="btn btn-sm btn-secondary">
                    <Download size={14} /> Download
                  </a>
                )}
                <button className="btn btn-sm btn-secondary" onClick={closePreview}>
                  <X size={14} /> Schließen
                </button>
              </div>
            </div>
            {previewUrl && previewIsPdf && (
              <object data={previewUrl} type="application/pdf" className="portal-preview-iframe" aria-label={previewTitle}>
                <p style={{ padding: 32, textAlign: 'center' }}>PDF kann nicht angezeigt werden. <a href={previewUrl} download={`${previewTitle}.pdf`}>Hier herunterladen</a></p>
              </object>
            )}
            {previewUrl && !previewIsPdf && <iframe src={previewUrl} className="portal-preview-iframe" title={previewTitle} />}
            {previewHtml && <iframe srcDoc={previewHtml} className="portal-preview-iframe" title={previewTitle} />}
          </div>
        )}
        </main>
      </div>
    </div>
  );
}
